import { API_BASE } from '../apiBase';
// src/projects/ProjectProfile_Docs&Media.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import exifr from 'exifr';
import jsPDF from 'jspdf';
import SafeReceiptCropper from './project_finances/ReceiptCropper';
import { Upload, FileText, Video, Image as ImageIcon, Trash2, Loader2, CheckCircle2, X, Play, ChevronLeft, ChevronRight, ArrowRightLeft, Search, Globe, LayoutGrid, List, Folder, ChevronDown, Heart } from 'lucide-react';
import { uploadStore } from '../lib/uploadStore';

// ==================== DIRECT-UPLOAD HELPERS ====================
const isHeic = (name) => /\.(heic|heif)$/i.test(name || '');

// Capture a video's opening frame as a JPEG in the browser (Dropbox's own
// video thumbnails pick an arbitrary frame, which made clips unrecognizable
// next to the camera roll). Black-frame guard: many clips start on black
// (fade-ins), and Safari sometimes hands over the frame before it's actually
// decoded — either way we'd save a solid-black poster that OVERRIDES the
// Dropbox thumbnail. So the captured frame is brightness-checked; if it's
// black we step forward (1s, then 3s) and only keep a frame with real
// content. Returns null when nothing usable comes out — the Dropbox
// thumbnail stays as the fallback.
const makeVideoPoster = (file) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    const timer = setTimeout(() => { URL.revokeObjectURL(url); resolve(null); }, 15000);
    const fail = () => { clearTimeout(timer); URL.revokeObjectURL(url); resolve(null); };
    const done = (b) => { clearTimeout(timer); URL.revokeObjectURL(url); resolve(b); };
    // avg sampled channel brightness < 10/255 → effectively a black frame
    const frameIsBlack = (ctx, w, h) => {
      try {
        const d = ctx.getImageData(0, 0, w, h).data;
        let sum = 0, count = 0;
        for (let i = 0; i < d.length; i += 4 * 97) { sum += d[i] + d[i + 1] + d[i + 2]; count++; }
        return count > 0 && (sum / (count * 3)) < 10;
      } catch (_) { return false; } // tainted/unreadable canvas: keep the frame
    };
    const tryTimes = [0.1, 1.0, 3.0];
    let attempt = 0;
    v.onloadedmetadata = () => { try { v.currentTime = tryTimes[0]; } catch (e) { fail(); } };
    v.onseeked = () => {
      // One paint tick between seek and draw — Safari can fire 'seeked'
      // before the new frame is actually ready, which draws black.
      requestAnimationFrame(() => {
        try {
          const scale = Math.min(1, 640 / (v.videoWidth || 640));
          const c = document.createElement('canvas');
          c.width = Math.max(1, Math.round((v.videoWidth || 640) * scale));
          c.height = Math.max(1, Math.round((v.videoHeight || 360) * scale));
          const ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(v, 0, 0, c.width, c.height);
          if (frameIsBlack(ctx, c.width, c.height)) {
            attempt++;
            const next = tryTimes[attempt];
            if (next != null && (!v.duration || next < v.duration)) { v.currentTime = next; return; }
            return done(null); // all-black clip (or never decoded) — let Dropbox's thumb stand
          }
          c.toBlob((b) => done(b), 'image/jpeg', 0.8);
        } catch (e) { fail(); }
      });
    };
    v.onerror = fail;
    v.src = url;
  } catch (e) { resolve(null); }
});

// ---- Module-level upload state ----
// Moved to lib/uploadStore (imported at top) so the floating UploadTray
// (mounted in App.js) can show progress + cancel from ANY page. It lives
// outside React, so uploads survive navigation; on remount this page
// re-subscribes and the progress bar picks up exactly where the upload is.

// PDF page-1 preview tile: fetches the pre-rendered .thumbs jpg through the
// authenticated thumbnail proxy and shows it as a tiny "paper" preview.
const DocPdfThumb = ({ path, className = '' }) => {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let alive = true; let url = null;
    fetchThumbBlob(path, 'w256h256')
      .then(u => { if (alive) { url = u; setSrc(u); } else URL.revokeObjectURL(u); })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <span className={`${className} bg-gray-800 animate-pulse inline-block`} />;
  return <img src={src} alt="" loading="lazy" className={className} draggable={false} />;
};

// Dropbox-generated thumbnail (works for images incl. HEIC, and videos —
// old uploads and new alike). Fetched with auth, returned as a blob URL.
const fetchThumbBlob = async (path, size = 'w480h320') => {
  const token = localStorage.getItem('token');
  const res = await axios.get(
    `${API_BASE}/media/thumbnail?path=${encodeURIComponent(path)}&size=${size}`,
    { headers: { Authorization: token }, responseType: 'blob' }
  );
  return URL.createObjectURL(res.data);
};

// Full-size display URL. HEIC can't render in browsers, so those use a large
// Dropbox-converted JPEG; everything else uses the raw temporary link.
const getDisplayUrl = async (path, name) => {
  if (isHeic(name)) return fetchThumbBlob(path, 'w2048h1536');
  const token = localStorage.getItem('token');
  const res = await axios.get(
    `${API_BASE}/projects/files/link?path=${encodeURIComponent(path)}`,
    { headers: { Authorization: token } }
  );
  return res.data.link;
};

// "2026-04-27_10.51.11" — formatted on the device so the timezone is the
// uploader's local time, matching the existing filename convention.
const formatLocalStamp = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}.${p(d.getMinutes())}.${p(d.getSeconds())}`;
};

// Caption for gallery/video tiles: the date the shot was TAKEN, not uploaded —
// "Sun, August 30, 2026". Prefers the taken-date the uploader baked into the
// filename (YYYY-MM-DD… prefix); falls back to the file's own device date;
// shows the filename only when no date is known. Hover still reveals the name.
const TILE_DATE_OPTS = { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' };
const tileCaption = (f) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(f.name || '');
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-US', TILE_DATE_OPTS);
  const d = f.client_modified || f.server_modified;
  if (d) return new Date(d).toLocaleDateString('en-US', TILE_DATE_OPTS);
  return f.name;
};

// Sub-component for individual Gallery Images
const ProjectImage = ({ img, index, isSelected, isLiked = false, onToggleLike = () => {}, onSelect, onPreview, deleteFile,
  menuOpen, onToggleMenu, projects = [], onMoveAllSelected, selectedCount = 0, moving = false }) => {
  const readOnlyViewer = (localStorage.getItem('role') || '') === 'employee';
  const [thumbUrl, setThumbUrl] = useState(null);
  const [fullUrl, setFullUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moveQuery, setMoveQuery] = useState('');           // filter the project list
  useEffect(() => { if (menuOpen) setMoveQuery(''); }, [menuOpen]); // reset each open

  const filteredProjects = (projects || []).filter(p => {
    const q = moveQuery.trim().toLowerCase();
    if (!q) return true;
    return String(p.poNumber || '').toLowerCase().includes(q) || String(p.title || '').toLowerCase().includes(q);
  });

  useEffect(() => {
    let cancelled = false;
    const fetchLinks = async () => {
      const path = img.path_lower;

      // Dropbox generates the thumbnail server-side — no pre-made
      // /thumbnails/ file needed (works for old uploads too).
      try {
        const url = await fetchThumbBlob(path);
        if (!cancelled) setThumbUrl(url);
      } catch (e) {}

      try {
        const url = await getDisplayUrl(path, img.name);
        if (!cancelled) setFullUrl(url);
      } catch (err) {}
      finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLinks();
    return () => { cancelled = true; };
  }, [img.path_lower, img.name]);

  // MOBILE: hold a photo (~0.5s) to enter select mode; while anything is
  // selected, plain taps toggle photos in and out (like iOS Photos).
  const holdTimer = useRef(null);
  const heldRef = useRef(false);
  const holdStart = useRef({ x: 0, y: 0 });
  const isCoarse = () => window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const onTilePointerDown = (e) => {
    if (e.pointerType === 'mouse') return;
    heldRef.current = false;
    holdStart.current = { x: e.clientX, y: e.clientY };
    holdTimer.current = setTimeout(() => {
      holdTimer.current = null;
      heldRef.current = true;             // eat the trailing click
      try { navigator.vibrate && navigator.vibrate(10); } catch (_) {}
      onSelect(index, 'toggle');
    }, 500);
  };
  const cancelHold = () => { if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; } };
  const onTilePointerMove = (e) => {
    if (!holdTimer.current) return;
    if (Math.hypot(e.clientX - holdStart.current.x, e.clientY - holdStart.current.y) > 10) cancelHold(); // scrolling
  };

  const handleClick = (e) => {
    if (heldRef.current) { heldRef.current = false; return; } // the tap that ended a hold
    if (e.shiftKey) {
      e.preventDefault();
      onSelect(index, 'range');           // shift = select everything between
    } else if (e.altKey || e.metaKey || e.ctrlKey) {
      e.preventDefault();
      onSelect(index, 'toggle');          // ⌘/alt = add or remove individual ones
    } else if (isCoarse() && selectedCount > 0) {
      onSelect(index, 'toggle');          // select mode on mobile: taps toggle
    } else if (fullUrl) {
      onPreview(fullUrl, index);
    }
  };

  return (
    <div>
    <div
      onClick={handleClick}
      onPointerDown={onTilePointerDown}
      onPointerMove={onTilePointerMove}
      onPointerUp={cancelHold}
      onPointerCancel={cancelHold}
      onContextMenu={(e) => { if (isCoarse()) e.preventDefault(); }}
      style={{ touchAction: 'pan-y', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
      className={`gallery-image-tile relative aspect-square rounded-2xl overflow-hidden group border-2 transition-all cursor-pointer ${
        isSelected ? 'border-indigo-500 ring-4 ring-indigo-500/20' : 'border-gray-800 bg-black hover:border-zinc-500'
      }`}
    >
      {/* Down-arrow — only while selected AND hovered (matches the trash icon).
          Once clicked it opens the menu and the arrow goes away; the list closes
          by clicking anywhere outside the thumbnail. */}
      {isSelected && !menuOpen && !readOnlyViewer && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleMenu(index); }}
          className="absolute top-2 right-2 z-40 p-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg shadow text-white opacity-0 group-hover:opacity-100 transition-all"
          title="Move to another project"
        >
          <ArrowRightLeft size={14} />
        </button>
      )}

      {/* Actions menu — a full-tile overlay so it stays WITHIN the thumbnail and
          scrolls instead of being clipped outside its bounds. */}
      {menuOpen && (
        <div
          className="gallery-move-menu absolute inset-0 z-50 bg-gray-900 flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 pr-3 border-b border-gray-700 shrink-0">
            <span className="text-[10px] uppercase tracking-wider text-zinc-400">Move All Selected ({selectedCount})</span>
          </div>
          {/* Searchable PO# / project-name filter */}
          <div className="px-2 py-2 border-b border-gray-800 shrink-0">
            <div className="flex items-center gap-1.5 bg-gray-800 rounded-lg px-2">
              <Search size={12} className="text-zinc-500 shrink-0" />
              <input
                autoFocus
                value={moveQuery}
                onChange={(e) => setMoveQuery(e.target.value)}
                placeholder="PO # or name…"
                className="w-full bg-transparent py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {projects.length === 0 ? (
              <div className="px-3 py-2 text-xs text-zinc-600">Loading projects…</div>
            ) : filteredProjects.length === 0 ? (
              <div className="px-3 py-2 text-xs text-zinc-600">No match</div>
            ) : filteredProjects.map(p => (
              <button
                key={p._id}
                disabled={moving}
                onClick={() => onMoveAllSelected(p._id, p.poNumber)}
                className="w-full text-left px-3 py-2 text-xs text-zinc-200 hover:bg-indigo-600/30 disabled:opacity-50 truncate border-b border-gray-800/50"
              >
                <span className="font-bold text-indigo-300">#{p.poNumber}</span>
                {p.title ? <span className="text-zinc-400"> · {p.title}</span> : ''}
              </button>
            ))}
          </div>
        </div>
      )}
      {thumbUrl ? (
        <img src={thumbUrl} draggable={false} onDragStart={(e) => e.preventDefault()} className="w-full h-full object-contain" alt={img.name} loading="lazy" />
      ) : loading ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-900 animate-pulse">
          <ImageIcon size={24} className="text-zinc-700" />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-900">
          <ImageIcon size={24} className="text-zinc-700" />
        </div>
      )}

      {isSelected && (
        <div className="absolute top-2 left-2 bg-indigo-500 text-white rounded-full p-1 shadow-lg z-20">
          <CheckCircle2 size={16} />
        </div>
      )}

      {/* Heart: always present, tap to toggle. White outline = not liked,
          solid pink = liked. Never opens the photo. */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleLike(img); }}
        onPointerDown={(e) => e.stopPropagation()}
        title={isLiked ? 'Unlike' : 'Like'}
        className="absolute bottom-2 right-2 z-20 p-1.5 drop-shadow-lg transition-transform active:scale-125"
      >
        <Heart size={16} className={isLiked ? 'text-pink-500 fill-pink-500' : 'text-white'} />
      </button>
    </div>

    {/* Name as a caption UNDER the thumbnail (was an overlay on the image) */}
    <div className="flex items-center gap-1.5 mt-1.5 px-0.5">
      {img.fromRelated && (
        <div title={`PO#${img.relatedPo}`}>
          <Globe size={12} className="text-[#39ff14] flex-shrink-0" />
        </div>
      )}
      <p className="text-zinc-300 text-xs truncate" title={img.name}>{tileCaption(img)}</p>
    </div>
    </div>
  );
};

// Sub-component for Video Cards
const VideoCard = ({ video, index, isSelected = false, isLiked = false, anySelected = false, onSelect = () => {}, onOpen, onDelete }) => {
  const [vThumbUrl, setVThumbUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchVideoThumb = async () => {
      // Dropbox generates video thumbnails server-side — no ffmpeg needed.
      try {
        const url = await fetchThumbBlob(video.path_lower);
        if (!cancelled) setVThumbUrl(url);
      } catch (e) {}
    };
    fetchVideoThumb();
    return () => { cancelled = true; };
  }, [video.path_lower]);

  // Same selection gestures as gallery tiles: desktop shift/alt-click,
  // mobile hold (~0.5s) then plain taps while anything is selected.
  const holdTimer = useRef(null);
  const heldRef = useRef(false);
  const holdStart = useRef({ x: 0, y: 0 });
  const isCoarse = () => window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const onTilePointerDown = (e) => {
    if (e.pointerType === 'mouse') return;
    heldRef.current = false;
    holdStart.current = { x: e.clientX, y: e.clientY };
    holdTimer.current = setTimeout(() => {
      holdTimer.current = null;
      heldRef.current = true;
      try { navigator.vibrate && navigator.vibrate(10); } catch (_) {}
      onSelect(index, 'toggle');
    }, 500);
  };
  const cancelHold = () => { if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; } };
  const onTilePointerMove = (e) => {
    if (!holdTimer.current) return;
    if (Math.hypot(e.clientX - holdStart.current.x, e.clientY - holdStart.current.y) > 10) cancelHold();
  };
  const handleClick = (e) => {
    if (heldRef.current) { heldRef.current = false; return; }
    if (e.shiftKey) { e.preventDefault(); onSelect(index, 'range'); return; }
    if (e.altKey || e.metaKey || e.ctrlKey) { e.preventDefault(); onSelect(index, 'toggle'); return; }
    if (isCoarse() && anySelected) { onSelect(index, 'toggle'); return; }
    onOpen(video.path_lower);
  };

  return (
    <div>
    <div
      className={`video-card-tile relative aspect-video max-md:aspect-square bg-black rounded-2xl overflow-hidden border-2 cursor-pointer group transition-all ${
        isSelected ? 'border-indigo-500 ring-4 ring-indigo-500/20' : 'border-gray-700 hover:border-indigo-500'
      }`}
      onClick={handleClick}
      onPointerDown={onTilePointerDown}
      onPointerMove={onTilePointerMove}
      onPointerUp={cancelHold}
      onPointerCancel={cancelHold}
      onContextMenu={(e) => { if (isCoarse()) e.preventDefault(); }}
      style={{ touchAction: 'pan-y', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
    >
      {vThumbUrl ? (
        <img src={vThumbUrl} draggable={false} onDragStart={(e) => e.preventDefault()} className="w-full h-full object-contain" alt="Video preview" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 to-black" />
      )}

      <div className="absolute bottom-3 left-3 bg-black/70 p-2 rounded-xl">
        <Play size={20} className="text-white" />
      </div>

      {isSelected && (
        <div className="absolute top-2 left-2 bg-indigo-500 text-white rounded-full p-1 shadow-lg z-20">
          <CheckCircle2 size={16} />
        </div>
      )}
      {isLiked && (
        <div className="absolute bottom-3 right-3 z-20 drop-shadow-lg">
          <Heart size={16} className="text-pink-500 fill-pink-500" />
        </div>
      )}
    </div>

    {/* Name as a caption UNDER the video (was an overlay on the thumbnail) */}
    <div className="flex items-center gap-1.5 mt-1.5 px-0.5">
      {video.fromRelated && (
        <div title={`PO#${video.relatedPo}`}>
          <Globe size={12} className="text-[#39ff14] flex-shrink-0" />
        </div>
      )}
      <p className="text-zinc-300 text-xs truncate" title={video.name}>{tileCaption(video)}</p>
    </div>
    </div>
  );
};

// MAIN COMPONENT
function ProjectProfile_DocsMedia({
  empPerms = null,
  id,
  files,
  setFiles,
  selectedIndices,
  setSelectedIndices,
  lastSelectedIndex,
  setLastSelectedIndex,
  fetchFiles,
  uploading,
  uploadProgress,
  setUploading,
  setUploadProgress,
  relatedProjects,
  currentPo,
}) {
  // Employees: view + upload only — no delete, rename, move, or folders.
  const isEmployeeViewer = (localStorage.getItem('role') || '') === 'employee';
  // Per-category employee permissions (from the Assigned Employees card).
  // 'none' hides a whole section; the server filters the file lists anyway.
  const hideDocsCat = empPerms?.documents === 'none';
  const hideVideosCat = empPerms?.videos === 'none';
  const hideGalleryCat = empPerms?.images === 'none';
  const navigate = useNavigate();
  // Globe on a global file → jump to that file's native project's Documents,
  // with Back reading "< PO#<this project>".
  const openNativeProject = (doc, e) => {
    e.stopPropagation();
    if (!doc.relatedProjectId) return;
    navigate(`/projects/${doc.relatedProjectId}`, {
      // docsGlobal: remember that global view was ON here, so "< PO#" brings
      // you back with it still on.
      state: { goToDocs: true, fromProject: { id, po: currentPo || '', docsGlobal: true } },
    });
  };

  // 1. STATE DECLARATIONS
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  // Documents view: 'list' (rows) or 'grid' (tiles) — remembered per device.
  const [docsView, setDocsView] = useState(() => localStorage.getItem('docsViewMode') || 'list');

  // ---- Virtual document folders (UI-only; Dropbox stays flat) ----
  const [docFolders, setDocFolders] = useState([]);
  const [openFolderId, setOpenFolderId] = useState(null);   // inside a folder (both views)
  // List view's expanded folders persist PER PROJECT on the USER RECORD in
  // the database — device-agnostic (same pattern as the finances summary
  // config): expand on desktop, it's expanded on your phone too, until closed.
  const [expandedFolders, setExpandedFolders] = useState({});
  const expLoadedRef = useRef(false);
  useEffect(() => {
    expLoadedRef.current = false;
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/me`, { headers: { Authorization: token } })
      .then((r) => { setExpandedFolders(r.data?.docFolderExpansion?.[id] || {}); })
      .catch(() => {})
      .finally(() => { expLoadedRef.current = true; });
  }, [id]);
  useEffect(() => {
    if (!expLoadedRef.current) return; // don't save until the load finished
    const token = localStorage.getItem('token');
    axios.put(`${API_BASE}/user/doc-expansion/${id}`, expandedFolders, { headers: { Authorization: token } }).catch(() => {});
  }, [expandedFolders, id]);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  // Hold-to-drag (iOS style): after a 2s press a file lifts and follows the pointer.
  const [dragDoc, setDragDoc] = useState(null);   // { path, name }
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [dropHint, setDropHint] = useState(null); // 'file:<path>' | 'folder:<id>' | 'out'
  const dragDocRef = useRef(null);
  const dropHintRef = useRef(null);
  const docPressTimer = useRef(null);
  // Multi-select (shift / cmd / ctrl click) — dragging any selected file
  // carries the whole selection, Finder-style.
  const [selectedDocs, setSelectedDocs] = useState([]);
  const selectedDocsRef = useRef([]);
  useEffect(() => { selectedDocsRef.current = selectedDocs; }, [selectedDocs]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelectedDocs([]); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Any plain click OUTSIDE the selected files clears the selection —
  // clicking blank space, a folder, another card, anywhere.
  useEffect(() => {
    if (selectedDocs.length === 0) return;
    const onAnyClick = (e) => {
      if (e.shiftKey || e.metaKey || e.ctrlKey) return;     // still building the selection
      if (Date.now() - justDraggedAt.current < 350) return; // the click that trails a drag
      if (e.target.closest && e.target.closest('.gallery-select-toolbar')) return; // toolbar actions keep the selection
      const el = e.target.closest && e.target.closest('[data-docdrop]');
      const key = el && el.getAttribute('data-docdrop');
      if (key && key.startsWith('file:') && selectedDocsRef.current.includes(key.slice(5))) return;
      setSelectedDocs([]);
    };
    document.addEventListener('click', onAnyClick);
    return () => document.removeEventListener('click', onAnyClick);
  }, [selectedDocs.length]);
  const toggleDocSelect = (path) => setSelectedDocs(prev =>
    prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  const justDraggedAt = useRef(0);
  // Tiny transient note shown when someone tries to move a global-view file.
  const [globalNote, setGlobalNote] = useState(null); // { x, y }
  const globalNoteTimer = useRef(null);
  const flashGlobalNote = (x, y) => {
    setGlobalNote({ x, y });
    clearTimeout(globalNoteTimer.current);
    globalNoteTimer.current = setTimeout(() => setGlobalNote(null), 1500);
  };
  // Finder semantics: SHIFT-click selects the visual RANGE from the last
  // click; ⌘-click (or Ctrl) toggles individuals in and out of the selection.
  const lastDocSelRef = useRef(null);
  const handleDocClick = (doc, e) => {
    if (dragDoc) return;
    if (renamingDocPath || renamingId) return; // mid-rename: drag-selecting text must never open/act
    if (Date.now() - justDraggedAt.current < 350) return; // the click that trails a drag
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      if (doc.fromRelated) { flashGlobalNote(e.clientX, e.clientY); return; } // can't join selections/folders
      // Selecting documents leaves the media world: clear photo/video picks.
      setSelectedIndices([]);
      setSelectedVideoIdx([]);
      if (e.shiftKey && lastDocSelRef.current && lastDocSelRef.current !== doc.path_lower) {
        // Range over the docs as rendered (expanded-folder members included in
        // list view), skipping globals — like dragging a Finder rubber band.
        const order = visibleDocOrder;
        const a = order.indexOf(lastDocSelRef.current);
        const b = order.indexOf(doc.path_lower);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          const range = order.slice(lo, hi + 1);
          setSelectedDocs(prev => [...new Set([...prev, ...range])]);
          return; // anchor stays put so a further shift-click extends from it
        }
      }
      toggleDocSelect(doc.path_lower); // ⌘/Ctrl (or first shift-click): toggle one
      lastDocSelRef.current = doc.path_lower;
      return;
    }
    setSelectedDocs([]);
    lastDocSelRef.current = null;
    openDocument(doc.path_lower);
  };

  const fetchDocFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/projects/${id}/doc-folders`, { headers: { Authorization: token } });
      setDocFolders(r.data || []);
    } catch (_) {}
  };
  useEffect(() => { if (id) fetchDocFolders(); /* eslint-disable-next-line */ }, [id]);

  const patchFolder = async (fid, body) => {
    if (isEmployeeViewer) return null;
    try {
      const token = localStorage.getItem('token');
      const r = await axios.patch(`${API_BASE}/doc-folders/${fid}`, body, { headers: { Authorization: token } });
      setDocFolders(prev => prev.map(f => f._id === fid ? r.data : f));
      return r.data;
    } catch (_) { return null; }
  };

  const deleteFolder = async (fid) => {
    if (isEmployeeViewer) return;
    if (!window.confirm('Remove this folder? The files inside go back to the main list (nothing is deleted).')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/doc-folders/${fid}`, { headers: { Authorization: token } });
      setDocFolders(prev => prev.filter(f => f._id !== fid));
      if (openFolderId === fid) setOpenFolderId(null);
    } catch (_) {}
  };

  // Long-press begins the drag; small movement before the timer fires means
  // the user is scrolling, so the press is cancelled. HOLD_MS: iOS uses ~500ms;
  // 2000ms felt broken (nothing seemed to happen), so 800ms + a visible
  // "arming" squeeze while you hold.
  const HOLD_MS = 800;
  const [pressingPath, setPressingPath] = useState(null);
  const startDocPress = (doc, e) => {
    if (doc.fromRelated) {
      // Not draggable — but say so only on a real drag ATTEMPT (press + move),
      // never on an ordinary click that just opens the file.
      const sx = e.clientX, sy = e.clientY;
      const off = () => {
        window.removeEventListener('pointermove', mv);
        window.removeEventListener('pointerup', off);
        window.removeEventListener('pointercancel', off);
      };
      const mv = (ev) => {
        if (Math.hypot(ev.clientX - sx, ev.clientY - sy) > 10) { off(); flashGlobalNote(ev.clientX, ev.clientY); }
      };
      window.addEventListener('pointermove', mv);
      window.addEventListener('pointerup', off);
      window.addEventListener('pointercancel', off);
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Stop the browser starting a text selection under the press — that was
    // painting blue highlights across the page during drags.
    if (e.pointerType === 'mouse') e.preventDefault();
    const startX = e.clientX, startY = e.clientY;

    // MOUSE: no hold at all — press and move a few pixels and the drag starts
    // instantly (a motionless click still just opens the file). The hold only
    // applies to TOUCH, where it separates dragging from scrolling.
    if (e.pointerType === 'mouse' || selectedDocsRef.current.includes(doc.path_lower)) {
      const armMove = (ev) => {
        if (Math.hypot(ev.clientX - startX, ev.clientY - startY) > 6) {
          disarm();
          beginDocDrag(doc, ev.clientX, ev.clientY);
        }
      };
      const disarm = () => {
        window.removeEventListener('pointermove', armMove);
        window.removeEventListener('pointerup', disarm);
        window.removeEventListener('pointercancel', disarm);
      };
      window.addEventListener('pointermove', armMove);
      window.addEventListener('pointerup', disarm);
      window.addEventListener('pointercancel', disarm);
      return;
    }

    clearTimeout(docPressTimer.current);
    setPressingPath(doc.path_lower);
    const cancel = () => {
      clearTimeout(docPressTimer.current);
      setPressingPath(null);
      window.removeEventListener('pointerup', cancel);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('pointermove', moveCheck);
    };
    const moveCheck = (ev) => {
      if (!dragDocRef.current && Math.hypot(ev.clientX - startX, ev.clientY - startY) > 12) cancel();
    };
    docPressTimer.current = setTimeout(() => { cancel(); beginDocDrag(doc, startX, startY); }, HOLD_MS);
    window.addEventListener('pointerup', cancel);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('pointermove', moveCheck);
  };

  const updateDropHint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const target = el && el.closest && el.closest('[data-docdrop]');
    let hint = target ? target.getAttribute('data-docdrop') : null;
    // Never target a file that's part of the drag itself.
    if (hint && hint.startsWith('file:') && dragDocRef.current?.paths?.includes(hint.slice(5))) hint = null;
    if (hint !== dropHintRef.current) { dropHintRef.current = hint; setDropHint(hint); }
  };

  const beginDocDrag = (doc, x, y) => {
    // If the pressed file is part of the current selection, the whole
    // selection rides along; otherwise just this file.
    const sel = selectedDocsRef.current;
    const paths = sel.includes(doc.path_lower) && sel.length > 0 ? [...sel] : [doc.path_lower];
    dragDocRef.current = { paths, name: doc.name, count: paths.length };
    setDragDoc(dragDocRef.current);
    setDragPos({ x, y });
    if (navigator.vibrate) navigator.vibrate(30);
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    try { window.getSelection()?.removeAllRanges(); } catch (_) {}
    const onMove = (ev) => { setDragPos({ x: ev.clientX, y: ev.clientY }); updateDropHint(ev.clientX, ev.clientY); };
    const stopScroll = (ev) => ev.preventDefault(); // keep the page still while dragging (touch)
    const onUp = async (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('touchmove', stopScroll);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      try { window.getSelection()?.removeAllRanges(); } catch (_) {}
      const hint = dropHintRef.current;
      const dragged = dragDocRef.current;
      dragDocRef.current = null; dropHintRef.current = null;
      setDragDoc(null); setDropHint(null);
      justDraggedAt.current = Date.now();
      if (!hint || !dragged) return;
      try {
        const token = localStorage.getItem('token');
        if (hint.startsWith('file:')) {
          // Drop on another file → new folder holding the dragged set + target.
          const other = hint.slice(5);
          const r = await axios.post(`${API_BASE}/projects/${id}/doc-folders`,
            { files: [...dragged.paths, other] }, { headers: { Authorization: token } });
          setDocFolders(prev => [...prev, r.data]);
          setRenamingId(r.data._id); setRenameVal(r.data.name); // invite a name right away
        } else if (hint.startsWith('folder:')) {
          await patchFolder(hint.slice(7), { addFiles: dragged.paths });
        } else if (hint === 'out' && openFolderId) {
          await patchFolder(openFolderId, { removeFiles: dragged.paths });
        }
        setSelectedDocs([]);
      } catch (_) {}
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('touchmove', stopScroll, { passive: false });
  };

  const commitRename = async () => {
    if (isEmployeeViewer) return;
    if (!renamingId) return;
    const fid = renamingId;
    setRenamingId(null);
    await patchFolder(fid, { name: renameVal });
  };

  // ---- Swipe-to-delete (mobile): swipe an item left to reveal Delete ----
  // Reveal-then-tap only (no full-swipe shortcut). One item open at a time.
  const [swipedKey, setSwipedKey] = useState(null);
  const [swipeState, setSwipeState] = useState({ key: null, dx: 0, active: false });
  const trackSwipe = (key, e) => {
    if (e.pointerType === 'mouse') return; // touch/pen only
    const sx = e.clientX, sy = e.clientY;
    let decided = null, lastDx = 0;
    const off = () => {
      window.removeEventListener('pointermove', mv);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    const mv = (ev) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      if (!decided) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        decided = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (decided === 'v' || dragDocRef.current) { off(); return; } // scrolling or mid-drag
      }
      lastDx = Math.max(-100, Math.min(20, dx));
      setSwipeState({ key, dx: Math.min(0, lastDx), active: true });
    };
    const up = () => {
      off();
      setSwipeState({ key: null, dx: 0, active: false });
      if (decided === 'h') {
        justDraggedAt.current = Date.now(); // eat the trailing click
        if (lastDx < -45) setSwipedKey(key);           // opened
        else setSwipedKey(k => (k === key ? null : k)); // closed / not enough
      }
    };
    window.addEventListener('pointermove', mv);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
  // Any tap elsewhere closes an open swipe.
  useEffect(() => {
    if (!swipedKey) return;
    const close = () => setSwipedKey(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [swipedKey]);

  const SwipeWrap = (key, onDelete, children, rounded = '') => {
    const open = swipedKey === key;
    const liveDx = swipeState.key === key ? swipeState.dx : 0;
    const dx = open ? Math.min(-72, liveDx || -72) : Math.min(0, liveDx);
    return (
      <div className={`relative overflow-hidden ${rounded}`}>
        {(open || dx < 0) && (
          <button
            onClick={(e) => { e.stopPropagation(); setSwipedKey(null); onDelete(e); }}
            className="absolute inset-y-0 right-0 w-20 bg-red-600 active:bg-red-500 text-white text-xs font-black uppercase flex items-center justify-center"
          >
            Delete
          </button>
        )}
        <div
          style={{ transform: `translateX(${dx}px)`, transition: swipeState.key === key && swipeState.active ? 'none' : 'transform 0.22s ease' }}
          className="relative bg-gray-900"
        >
          {children}
        </div>
      </div>
    );
  };

  // ---- Renderers for the folder-aware Documents card ----
  const dragging = (p) => dragDoc && dragDoc.paths.includes(p);
  // PDF page-1 preview: the .thumbs jpg rides in the same recursive file list.
  const docThumbPathOf = React.useMemo(() => {
    const m = new Map();
    files.forEach(f => {
      if (f.path_lower && f.path_lower.includes('/documents/.thumbs/')) {
        m.set(f.name.replace(/\.jpg$/i, ''), f.path_lower);
      }
    });
    return (doc) => (/\.pdf$/i.test(doc.name) ? m.get(doc.name.replace(/\.pdf$/i, '')) || null : null);
  }, [files]);

  const isSelectedDoc = (p) => selectedDocs.includes(p);
  const hintCls = (key) => dropHint === key ? ' ring-2 ring-indigo-400 scale-[1.02] transition-transform' : ' transition-transform';

  const DocRowFile = (doc, indent = false) => {
    const d = doc.client_modified || doc.server_modified;
    const when = d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    return (
      <React.Fragment key={doc.path_lower}>
      {SwipeWrap(`file:${doc.path_lower}`, (e) => deleteFile(doc.path_lower, e, doc.fromRelated, doc.relatedPo, doc.relatedProjectId),
      <div
        {...(!doc.fromRelated ? { 'data-docdrop': `file:${doc.path_lower}` } : {})}
        onPointerDown={(e) => { trackSwipe(`file:${doc.path_lower}`, e); startDocPress(doc, e); }}
        onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => handleDocClick(doc, e)}
        style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none', touchAction: 'pan-y' }}
        className={`flex items-center gap-3 px-2 py-2 cursor-pointer group ${indent ? 'pl-10' : ''} ${isSelectedDoc(doc.path_lower) ? 'bg-indigo-500/15 ring-1 ring-inset ring-indigo-500/40' : 'hover:bg-gray-800/30'} ${dragging(doc.path_lower) ? 'opacity-30' : ''} ${pressingPath === doc.path_lower ? 'scale-[0.98] opacity-70' : ''}${hintCls(`file:${doc.path_lower}`)}`}
      >
        {docThumbPathOf(doc)
          ? <DocPdfThumb path={docThumbPathOf(doc)} className="w-7 h-9 rounded object-cover object-top bg-white shrink-0" />
          : <FileText size={16} className="text-red-500 shrink-0" />}
        {doc.fromRelated && (
          <button onClick={(e) => openNativeProject(doc, e)} title={`Open in PO#${doc.relatedPo}`} className="shrink-0 hover:scale-125 transition-transform">
            <Globe size={12} className="text-[#39ff14]" />
          </button>
        )}
        {/* wrapper takes the stretch; the NAME itself only spans its own text,
            so rename can only trigger by clicking the actual letters */}
        <div className="flex-1 min-w-0">
          {DocNameEl(doc, 'inline-block max-w-full align-bottom truncate text-sm text-zinc-200 hover:underline underline-offset-2')}
        </div>
        {likedMedia.includes(doc.path_lower) && <Heart size={12} className="text-pink-500 fill-pink-500 shrink-0" />}
        <span className="text-xs text-zinc-500 shrink-0 max-md:hidden">{when}</span>
      </div>
      )}
      </React.Fragment>
    );
  };

  // ---- FILE rename (this one DOES rename the real Dropbox file) ----
  const [renamingDocPath, setRenamingDocPath] = useState(null);
  const [renameDocVal, setRenameDocVal] = useState('');
  const lastDocNameTap = useRef({ p: null, t: 0 });
  const docNameTapTimer = useRef(null);
  const handleDocNameTap = (doc, e) => {
    e.stopPropagation();
    if (doc.fromRelated) { handleDocClick(doc, e); return; } // global files: tap just opens
    if (Date.now() - justDraggedAt.current < 350) return; // swipe/drag just ended — not a tap
    const start = () => {
      // Edit the BASE name only — the extension is fixed and shown beside the
      // input, not part of the editable text.
      const ext = ((doc.name || '').match(/\.[a-z0-9]+$/i) || [''])[0];
      setRenamingDocPath(doc.path_lower);
      setRenameDocVal(ext ? doc.name.slice(0, doc.name.length - ext.length) : doc.name);
    };
    const isTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (!isTouch) return start();
    // MOBILE: single tap OPENS the file (after a short double-tap window);
    // a second tap within that window cancels the open and starts a rename.
    const now = Date.now();
    if (lastDocNameTap.current.p === doc.path_lower && now - lastDocNameTap.current.t < 400) {
      lastDocNameTap.current = { p: null, t: 0 };
      if (docNameTapTimer.current) { clearTimeout(docNameTapTimer.current); docNameTapTimer.current = null; }
      start();
    } else {
      lastDocNameTap.current = { p: doc.path_lower, t: now };
      if (docNameTapTimer.current) clearTimeout(docNameTapTimer.current);
      docNameTapTimer.current = setTimeout(() => {
        docNameTapTimer.current = null;
        handleDocClick(doc, e);
      }, 320);
    }
  };
  const commitDocRename = async () => {
    if (isEmployeeViewer) return;
    const path = renamingDocPath;
    const name = renameDocVal.trim();
    setRenamingDocPath(null);
    if (!path || !name) return;

    // Mirror the server's cleanup so the OPTIMISTIC update matches what will
    // actually happen: sanitize + keep the original extension.
    const doc = documentFiles.find(d => d.path_lower === path);
    if (!doc) return; // can't verify a change — never fire a blind rename
    const oldExt = ((doc.name || '').match(/\.[a-z0-9]+$/i) || [''])[0];
    // Spaces are fine while TYPING, but the committed filename has none —
    // they're stripped out (use CamelCase or dashes for word breaks).
    let clean = name.replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '');
    if (!clean) return;
    if (oldExt && !clean.toLowerCase().endsWith(oldExt.toLowerCase())) clean += oldExt;
    // UNCHANGED name (any spelling of it) = no Dropbox call, no error, nothing.
    if (name === doc.name || clean === doc.name || clean.toLowerCase() === doc.name.toLowerCase()) return;
    const dir = path.substring(0, path.lastIndexOf('/'));
    const expectedLower = `${dir}/${clean}`.toLowerCase();

    // INSTANT: rename in the UI now; the server catches up in the background.
    setFiles(prev => (prev || []).map(f => f.path_lower === path ? { ...f, name: clean, path_lower: expectedLower } : f));
    setDocFolders(prev => prev.map(fl => ({ ...fl, files: fl.files.map(p => p === path ? expectedLower : p) })));

    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_BASE}/projects/${id}/documents/rename`, { path, newName: name }, { headers: { Authorization: token } });
      // Rare: Dropbox auto-suffixed on a name conflict — reconcile quietly.
      if (r.data?.path_lower && r.data.path_lower !== expectedLower) {
        setFiles(prev => (prev || []).map(f => f.path_lower === expectedLower ? { ...f, name: r.data.name, path_lower: r.data.path_lower } : f));
        setDocFolders(prev => prev.map(fl => ({ ...fl, files: fl.files.map(p => p === expectedLower ? r.data.path_lower : p) })));
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Could not rename the file.');
      fetchFiles();       // roll the UI back to the truth
      fetchDocFolders();
    }
  };
  const DocNameEl = (doc, cls) => renamingDocPath === doc.path_lower ? (
    <span className="flex items-center gap-1 w-full min-w-0" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <input
        autoFocus
        value={renameDocVal}
        onChange={(e) => setRenameDocVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commitDocRename(); if (e.key === 'Escape') setRenamingDocPath(null); }}
        onBlur={commitDocRename}
        className="bg-gray-800 border border-indigo-500/60 rounded px-2 py-0.5 text-sm text-white outline-none flex-1 min-w-0"
      />
      {/* fixed, uneditable extension */}
      <span className="text-sm text-zinc-500 shrink-0">{((doc.name || '').match(/\.[a-z0-9]+$/i) || [''])[0]}</span>
    </span>
  ) : (
    <p
      onClick={(e) => handleDocNameTap(doc, e)}
      // DESKTOP: eat pointerdown so a click on the name renames instead of
      // arming a drag. MOBILE: let it through — the row needs it to detect
      // swipe-left (delete) and hold (drag); rename is double-tap there.
      onPointerDown={(e) => {
        if (doc.fromRelated) return;
        const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        if (!coarse) e.stopPropagation();
      }}
      title={doc.fromRelated ? doc.name : `${doc.name} — click to rename (double-tap on mobile)`}
      className={cls}
    >
      {doc.name}
    </p>
  );

  // Rename triggers Finder-style: DESKTOP a single click on the name starts
  // renaming; MOBILE it takes a double-tap (single taps are too easy to graze).
  const lastNameTap = useRef({ id: null, t: 0 });
  const handleNameTap = (f, e) => {
    e.stopPropagation();
    const isTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (!isTouch) { setRenamingId(f._id); setRenameVal(f.name); return; }
    const now = Date.now();
    if (lastNameTap.current.id === f._id && now - lastNameTap.current.t < 400) {
      lastNameTap.current = { id: null, t: 0 };
      setRenamingId(f._id); setRenameVal(f.name);
    } else {
      lastNameTap.current = { id: f._id, t: now };
    }
  };

  const FolderName = (f) => renamingId === f._id ? (
    <input
      autoFocus
      value={renameVal}
      onChange={(e) => setRenameVal(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingId(null); }}
      onBlur={commitRename}
      className="bg-gray-800 border border-indigo-500/60 rounded px-2 py-0.5 text-sm text-white outline-none w-40"
    />
  ) : (
    <span
      onClick={(e) => handleNameTap(f, e)}
      title="Click to rename (double-tap on mobile)"
      className="text-sm font-semibold text-zinc-100 truncate hover:underline underline-offset-2"
    >
      {f.name}
    </span>
  );

  const DocRowFolder = (f) => (
    <React.Fragment key={f._id}>
      {SwipeWrap(`folder:${f._id}`, () => deleteFolder(f._id),
      <div
        data-docdrop={`folder:${f._id}`}
        onPointerDown={(e) => trackSwipe(`folder:${f._id}`, e)}
        onClick={() => { if (renamingId || renamingDocPath) return; if (Date.now() - justDraggedAt.current < 350) return; setExpandedFolders(prev => ({ ...prev, [f._id]: !prev[f._id] })); }}
        onDoubleClick={() => { if (renamingId || renamingDocPath) return; setOpenFolderId(f._id); }}
        title="Click to expand · double-click to open"
        style={{ touchAction: 'pan-y' }}
        className={`flex items-center gap-3 px-2 py-2 hover:bg-gray-800/30 cursor-pointer group${hintCls(`folder:${f._id}`)}`}
      >
        {expandedFolders[f._id]
          ? <ChevronDown size={14} className="text-zinc-500 shrink-0" />
          : <ChevronRight size={14} className="text-zinc-500 shrink-0" />}
        <Folder size={16} className="text-indigo-400 shrink-0" />
        {FolderName(f)}
        <span className="text-xs text-zinc-500">({f.files.length})</span>
        <span className="flex-1" />
        {!isEmployeeViewer && (
        <button
          onClick={(e) => { e.stopPropagation(); deleteFolder(f._id); }}
          title="Remove folder (files go back to the list)"
          className="max-md:hidden text-zinc-700 hover:text-red-500 opacity-0 group-hover:opacity-100 p-1 transition-all shrink-0"
        >
          <Trash2 size={14} />
        </button>
        )}
      </div>
      )}
      {expandedFolders[f._id] && folderMembers(f).map(d => DocRowFile(d, true))}
    </React.Fragment>
  );

  const DocTileFile = (doc) => (
    <React.Fragment key={doc.path_lower}>
    {SwipeWrap(`file:${doc.path_lower}`, (e) => deleteFile(doc.path_lower, e, doc.fromRelated, doc.relatedPo, doc.relatedProjectId),
    <div
      {...(!doc.fromRelated ? { 'data-docdrop': `file:${doc.path_lower}` } : {})}
      onPointerDown={(e) => { trackSwipe(`file:${doc.path_lower}`, e); startDocPress(doc, e); }}
      onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => handleDocClick(doc, e)}
      style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none', touchAction: 'pan-y' }}
      className={`flex items-center justify-between p-4 rounded-2xl border group cursor-pointer ${isSelectedDoc(doc.path_lower) ? 'border-indigo-500/70 bg-indigo-500/15' : 'bg-gray-800/30 border-gray-800/50 hover:border-indigo-500/40'} ${dragging(doc.path_lower) ? 'opacity-30' : ''} ${pressingPath === doc.path_lower ? 'scale-[0.98] opacity-70' : ''}${hintCls(`file:${doc.path_lower}`)}`}
    >
      <div className="flex items-center gap-4 min-w-0">
        {docThumbPathOf(doc)
          ? <DocPdfThumb path={docThumbPathOf(doc)} className="w-11 h-14 rounded-lg object-cover object-top bg-white shrink-0" />
          : <div className="p-3 bg-red-500/10 text-red-500 rounded-xl shrink-0"><FileText size={20} /></div>}
        <div className="flex items-center gap-2 min-w-0">
          {doc.fromRelated && (
            <button onClick={(e) => openNativeProject(doc, e)} title={`Open in PO#${doc.relatedPo}`} className="shrink-0 hover:scale-125 transition-transform">
              <Globe size={14} className="text-[#39ff14]" />
            </button>
          )}
          <div className="min-w-0">
            {DocNameEl(doc, 'inline-block max-w-full align-bottom truncate font-bold text-zinc-200 text-sm hover:underline underline-offset-2')}
          </div>
          {likedMedia.includes(doc.path_lower) && <Heart size={12} className="text-pink-500 fill-pink-500 shrink-0" />}
        </div>
      </div>
    </div>
    , 'rounded-2xl')}
    </React.Fragment>
  );

  const DocTileFolder = (f) => (
    <React.Fragment key={f._id}>
    {SwipeWrap(`folder:${f._id}`, () => deleteFolder(f._id),
    <div
      data-docdrop={`folder:${f._id}`}
      onPointerDown={(e) => trackSwipe(`folder:${f._id}`, e)}
      onDoubleClick={() => { if (renamingId || renamingDocPath) return; if (Date.now() - justDraggedAt.current < 350) return; setOpenFolderId(f._id); }}
      title="Double-click to open"
      style={{ touchAction: 'pan-y' }}
      className={`flex items-center justify-between p-4 bg-indigo-500/5 rounded-2xl border border-indigo-500/25 hover:border-indigo-500/60 group cursor-pointer${hintCls(`folder:${f._id}`)}`}
    >
      <div className="flex items-center gap-4 min-w-0">
        <div className="p-3 bg-indigo-500/10 text-indigo-400 rounded-xl shrink-0"><Folder size={20} /></div>
        <div className="min-w-0">
          {FolderName(f)}
          <p className="text-xs text-zinc-500 mt-0.5">{f.files.length} file{f.files.length === 1 ? '' : 's'}</p>
        </div>
      </div>
      {!isEmployeeViewer && (
      <button
        onClick={(e) => { e.stopPropagation(); deleteFolder(f._id); }}
        title="Remove folder (files go back to the list)"
        className="max-md:hidden text-zinc-700 hover:text-red-500 opacity-0 group-hover:opacity-100 p-2 transition-all shrink-0"
      >
        <Trash2 size={18} />
      </button>
      )}
    </div>
    , 'rounded-2xl')}
    </React.Fragment>
  );
  // Move-to-project menu state
  const [galleryProjects, setGalleryProjects] = useState([]);
  const [moveMenuIndex, setMoveMenuIndex] = useState(null);
  const [movingFiles, setMovingFiles] = useState(false);

  // Lightbox & Carousel State
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [touchActive, setTouchActive] = useState(false); // finger on the lightbox image
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  // Global View State
  const [isGlobalViewDocs, setIsGlobalViewDocs] = useState(false);
  // Returning via "< PO#" from a globe-jump: global view was on when you
  // left, so it comes back on.
  const location = useLocation();
  useEffect(() => {
    if (location.state?.restoreGlobalDocs) setIsGlobalViewDocs(true);
  }, [location.state]);
  const [isGlobalViewVideos, setIsGlobalViewVideos] = useState(false);
  const [isGlobalViewGallery, setIsGlobalViewGallery] = useState(false);
  // Easter-egg drop targets: OS files dragged over the Gallery/Videos cards.
  const [galleryDragging, setGalleryDragging] = useState(false);
  const [videosDragging, setVideosDragging] = useState(false);

  // ---- Gallery hearts: liked image paths (saved on the project) + a filter
  // toggle in the header. Optimistic: UI flips instantly, saves behind. ----
  const [likedMedia, setLikedMedia] = useState([]);
  const [showLikedOnly, setShowLikedOnly] = useState(false);
  const [showLikedVideosOnly, setShowLikedVideosOnly] = useState(false);

  // Video selection — same gestures as the gallery, its own index list.
  const [selectedVideoIdx, setSelectedVideoIdx] = useState([]);
  const lastVideoSel = useRef(null);
  const handleVideoSelect = (index, mode) => {
    setSelectedDocs([]); // media selection leaves the documents world
    setSelectedVideoIdx(prev => {
      if (mode === 'range' && lastVideoSel.current != null) {
        const [a, b] = [lastVideoSel.current, index].sort((x, y) => x - y);
        const range = [];
        for (let i = a; i <= b; i++) range.push(i);
        return [...new Set([...prev, ...range])];
      }
      lastVideoSel.current = index;
      return prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index];
    });
  };
  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/projects/${id}/media-likes`, { headers: { Authorization: token } })
      .then(r => setLikedMedia(r.data || []))
      .catch(() => setLikedMedia([]));
  }, [id]);
  const patchLikes = (add, remove) => {
    setLikedMedia(prev => {
      const s = new Set(prev);
      add.forEach(p => s.add(p));
      remove.forEach(p => s.delete(p));
      return [...s];
    });
    const token = localStorage.getItem('token');
    axios.post(`${API_BASE}/projects/${id}/media-likes`, { add, remove }, { headers: { Authorization: token } })
      .catch(() => {
        axios.get(`${API_BASE}/projects/${id}/media-likes`, { headers: { Authorization: token } })
          .then(r => setLikedMedia(r.data || [])).catch(() => {});
      });
  };
  const [relatedFiles, setRelatedFiles] = useState([]);

  // 2. DATA FETCHING (Global Files)
  useEffect(() => {
    const anyGlobalActive = isGlobalViewDocs || isGlobalViewVideos || isGlobalViewGallery;
    if (anyGlobalActive && relatedFiles.length === 0 && relatedProjects?.length > 0) {
      const fetchAllRelated = async () => {
        try {
          const token = localStorage.getItem('token');
          const allRelatedFiles = [];
          for (const rp of relatedProjects) {
            const res = await axios.get(`${API_BASE}/projects/${rp._id}/files`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const rpFiles = res.data.map(f => ({
              ...f,
              fromRelated: true,
              relatedPo: rp.poNumber,
              relatedProjectId: rp._id
            }));
            allRelatedFiles.push(...rpFiles);
          }
          setRelatedFiles(allRelatedFiles);
        } catch (e) {
          console.error("Failed to fetch global files", e);
        }
      };
      fetchAllRelated();
    }
  }, [isGlobalViewDocs, isGlobalViewVideos, isGlobalViewGallery, relatedProjects, relatedFiles.length]);

  // 3. FILE FILTERING & MERGING
  const documentFiles = useMemo(() => {
    const locals = files.filter(f => f && f.name && /\.(pdf|doc|docx|xls|xlsx|txt)$/i.test(f.name) && !f.path_lower.includes('/.thumbs/'));
    const globals = relatedFiles.filter(f => f && f.name && /\.(pdf|doc|docx|xls|xlsx|txt)$/i.test(f.name));
    return isGlobalViewDocs ? [...locals, ...globals] : locals;
  }, [files, relatedFiles, isGlobalViewDocs]);

  // Folder-aware document lists: at root, files inside folders are hidden
  // (they live "in" their folder); inside a folder, only its members show.
  const folderedSet = useMemo(() => new Set(docFolders.flatMap(f => f.files)), [docFolders]);
  const openF = docFolders.find(f => f._id === openFolderId) || null;
  const rootDocs = useMemo(() => documentFiles.filter(d => !folderedSet.has(d.path_lower)), [documentFiles, folderedSet]);
  const docsShown = openF ? documentFiles.filter(d => openF.files.includes(d.path_lower)) : rootDocs;
  const folderMembers = (f) => documentFiles.filter(d => f.files.includes(d.path_lower));

  // Every selectable document path in the exact order rendered on screen —
  // expanded-folder members first (list view shows them inline), then the
  // root files. Globals excluded (they can't be selected). Powers shift-range.
  const visibleDocOrder = useMemo(() => {
    const order = [];
    if (!openF && docsView === 'list') {
      docFolders.forEach(f => {
        if (expandedFolders[f._id]) folderMembers(f).forEach(d => { if (!d.fromRelated) order.push(d.path_lower); });
      });
    }
    docsShown.forEach(d => { if (!d.fromRelated) order.push(d.path_lower); });
    return order;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docFolders, expandedFolders, docsShown, openF, docsView]);

  const videoFiles = useMemo(() => {
    const locals = files.filter(f => f && f.name && /\.(mp4|mov|avi|wmv|webm|mkv)$/i.test(f.name));
    const globals = relatedFiles.filter(f => f && f.name && /\.(mp4|mov|avi|wmv|webm|mkv)$/i.test(f.name));
    const all = isGlobalViewVideos ? [...locals, ...globals] : locals;
    if (showLikedVideosOnly) {
      const liked = new Set(likedMedia);
      return all.filter(f => liked.has(f.path_lower));
    }
    return all;
  }, [files, relatedFiles, isGlobalViewVideos, showLikedVideosOnly, likedMedia]);

  const imageFiles = useMemo(() => {
    // /.thumbs/ holds auto-generated video posters — never gallery content.
    const isRealImage = (f) => f && f.name && /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(f.name)
      && !f.path_lower.includes('/thumbnails/') && !f.path_lower.includes('/.thumbs/');
    const locals = files.filter(isRealImage);
    const globals = relatedFiles.filter(isRealImage);
    const all = isGlobalViewGallery ? [...locals, ...globals] : locals;
    // Heart filter: show only hearted images while the header heart is on.
    if (showLikedOnly) {
      const liked = new Set(likedMedia);
      return all.filter(f => liked.has(f.path_lower));
    }
    return all;
  }, [files, relatedFiles, isGlobalViewGallery, showLikedOnly, likedMedia]);

  const groupedImages = useMemo(() => {
    const groups = {};
    
    imageFiles.forEach((img, idx) => {
      let monthName = "Archived";

      const nameMatch = img.name.match(/^(\d{4})-(\d{2})/);
      if (nameMatch) {
        const date = new Date(nameMatch[1], parseInt(nameMatch[2]) - 1);
        monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      } else if (img.server_modified || img.client_modified) {
        const date = new Date(img.client_modified || img.server_modified);
        monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      }

      if (!groups[monthName]) groups[monthName] = [];
      groups[monthName].push({ ...img, originalIndex: idx });
    });
    
    return groups;
  }, [imageFiles]);

  // Videos grouped by month, exactly like the gallery (filename date first,
  // then file dates, else "Archived").
  const groupedVideos = useMemo(() => {
    const groups = {};
    videoFiles.forEach((video, idx) => {
      let monthName = "Archived";
      const nameMatch = video.name.match(/^(\d{4})-(\d{2})/);
      if (nameMatch) {
        const date = new Date(nameMatch[1], parseInt(nameMatch[2]) - 1);
        monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      } else if (video.server_modified || video.client_modified) {
        const date = new Date(video.client_modified || video.server_modified);
        monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      }
      if (!groups[monthName]) groups[monthName] = [];
      groups[monthName].push({ ...video, originalIndex: idx });
    });
    return groups;
  }, [videoFiles]);

  // The originalIndex of every image in the exact order they're rendered on
  // screen — so a shift-range selects everything VISUALLY between two clicks,
  // even across month dividers.
  const visualOrder = useMemo(() => {
    const order = [];
    Object.values(groupedImages).forEach(imgs => imgs.forEach(img => order.push(img.originalIndex)));
    return order;
  }, [groupedImages]);

  // 4. LIGHTBOX EFFECTS & LOGIC
  const closeLightbox = () => {
    setLightboxImage(null);
    setLightboxIndex(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeLightbox();
        setSelectedVideoUrl(null);
      }
    };

    if (lightboxImage || selectedVideoUrl) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxImage, selectedVideoUrl]);

  const openImage = (url, index) => {
    setLightboxImage(url);
    setLightboxIndex(index);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    if (!lightboxImage) return;
    e.preventDefault();

    const scaleAmount = 0.2;
    const delta = e.deltaY > 0 ? -scaleAmount : scaleAmount;
    const nextZoom = Math.max(1, Math.min(10, zoom + delta));

    if (nextZoom === zoom) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const imageRelativeX = (mouseX - centerX - pan.x) / zoom;
    const imageRelativeY = (mouseY - centerY - pan.y) / zoom;

    setZoom(nextZoom);

    if (nextZoom <= 1) {
      setPan({ x: 0, y: 0 });
    } else {
      setPan({
        x: mouseX - centerX - imageRelativeX * nextZoom,
        y: mouseY - centerY - imageRelativeY * nextZoom
      });
    }
  };

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    hasMoved.current = false;
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const dx = e.clientX - lastMousePos.x;
    const dy = e.clientY - lastMousePos.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasMoved.current = true;
    setPan({ x: pan.x + dx, y: pan.y + dy });
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // ---- Touch gestures (mobile lightbox) ----
  // One finger: swipe left/right to change photos (at 1x), drag to pan (zoomed).
  // Two fingers: pinch to zoom. Desktop mouse behavior above is untouched.
  const touchState = useRef({ mode: null, startX: 0, startY: 0, startDist: 0, startZoom: 1, startPan: { x: 0, y: 0 }, lastX: 0, lastY: 0 });

  const handleTouchStart = (e) => {
    setTouchActive(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      touchState.current = {
        ...touchState.current,
        mode: 'pinch',
        startDist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        startZoom: zoom,
        startPan: { ...pan },
        // Pinch midpoint relative to the container CENTER — the anchor the
        // zoom grows around, so it magnifies where the fingers are.
        startMid: { x: (a.clientX + b.clientX) / 2 - cx, y: (a.clientY + b.clientY) / 2 - cy },
        center: { x: cx, y: cy },
      };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      touchState.current = {
        ...touchState.current,
        mode: zoom > 1 ? 'pan' : 'swipe',
        startX: t.clientX, startY: t.clientY,
        lastX: t.clientX, lastY: t.clientY,
        startPan: { ...pan },
      };
    }
    hasMoved.current = false;
  };

  const handleTouchMove = (e) => {
    const st = touchState.current;
    if (st.mode === 'pinch' && e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const next = Math.max(1, Math.min(10, st.startZoom * (dist / (st.startDist || 1))));
      setZoom(next);
      if (next <= 1) {
        setPan({ x: 0, y: 0 });
      } else {
        // Keep the image point under the fingers pinned under the fingers:
        // zoom toward the pinch, and let a two-finger drag move the image too.
        const mid = { x: (a.clientX + b.clientX) / 2 - st.center.x, y: (a.clientY + b.clientY) / 2 - st.center.y };
        const s = next / (st.startZoom || 1);
        setPan({
          x: mid.x - s * (st.startMid.x - st.startPan.x),
          y: mid.y - s * (st.startMid.y - st.startPan.y),
        });
      }
      hasMoved.current = true;
    } else if (st.mode === 'pan' && e.touches.length === 1) {
      const t = e.touches[0];
      setPan({ x: st.startPan.x + (t.clientX - st.startX), y: st.startPan.y + (t.clientY - st.startY) });
      hasMoved.current = true;
    } else if (st.mode === 'swipe' && e.touches.length === 1) {
      const t = e.touches[0];
      st.lastX = t.clientX;
      st.lastY = t.clientY;
      if (Math.abs(t.clientX - st.startX) > 10) hasMoved.current = true;
    }
  };

  const handleTouchEnd = (e) => {
    const st = touchState.current;
    // One finger lifted after a pinch: keep going as a PAN with the finger
    // still down — no need to lift and re-touch to move around.
    if (e.touches && e.touches.length === 1) {
      const t = e.touches[0];
      touchState.current = {
        ...touchState.current,
        mode: zoom > 1 ? 'pan' : 'swipe',
        startX: t.clientX, startY: t.clientY,
        lastX: t.clientX, lastY: t.clientY,
        startPan: { ...pan },
      };
      return;
    }
    if (st.mode === 'swipe') {
      const dx = st.lastX - st.startX;
      const dy = st.lastY - st.startY;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        navigateLightbox(dx < 0 ? 1 : -1);
      }
    }
    touchState.current.mode = null;
    setTouchActive(false);
  };

  const navigateLightbox = (direction) => {
    if (zoom > 1) return;
    if (lightboxIndex === null) return;

    let newIndex = lightboxIndex + direction;
    if (newIndex < 0) newIndex = imageFiles.length - 1;
    if (newIndex >= imageFiles.length) newIndex = 0;

    const nextImg = imageFiles[newIndex];
    getDisplayUrl(nextImg.path_lower, nextImg.name).then(url => {
      setLightboxImage(url);
      setLightboxIndex(newIndex);
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }).catch(() => {});
  };

  const deleteFile = async (path, e, fromRelated = false, relatedPo = '', relatedProjectId = null) => {
    if (isEmployeeViewer) return; // view + upload only
    if (fromRelated) {
      if (!e || !e.shiftKey || !(e.metaKey || e.ctrlKey)) {
        alert(`ACCESS DENIED: This media belongs to another project (PO#${relatedPo}).\n\nTo override and delete this globally, hold Shift + Command (or Ctrl on Windows) and click the trash icon.`);
        return;
      }
      if (!window.confirm(`⚠️ OVERRIDE ENGAGED: Are you sure you want to permanently delete this media from PO#${relatedPo}?`)) return;
    } else {
      if (!window.confirm('Delete this file? This will also remove its thumbnail.')) return;
    }

    const targetProjectId = fromRelated ? relatedProjectId : id;
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API_BASE}/projects/${targetProjectId}/files`, { 
        data: { path }, 
        headers: { Authorization: token } 
      });

      let thumbPath = null;
      if (path.includes('/images/')) {
        thumbPath = path.replace('/images/', '/images/thumbnails/').replace(/\.\w+$/, '.webp');
      } else if (path.includes('/videos/')) {
        thumbPath = path.replace('/videos/', '/videos/thumbnails/').replace(/\.\w+$/, '.webp');
      }

      if (thumbPath) {
        await axios.delete(`${API_BASE}/projects/${targetProjectId}/files`, { 
          data: { path: thumbPath }, 
          headers: { Authorization: token } 
        }).catch(() => console.log("No thumbnail found to clean up."));
      }

      if (fromRelated) {
        setRelatedFiles(prev => prev.filter(f => f.path_lower !== path));
      } else {
        fetchFiles();
      }
    } catch (err) {
      alert("Delete failed");
    }
  };

  // Dropbox's single-shot upload cap is 150 MB; bigger files (long videos)
  // fall back to the old server-side route, which streams them through.
  const LARGE_FILE_LIMIT = 140 * 1024 * 1024;
  const UPLOAD_CONCURRENCY = 4;

  // The browser takes a few seconds to materialize dropped files (macOS
  // exports them first, especially HEIC batches from Photos) BEFORE the app
  // receives them. 'preparing' covers that gap so the drop never looks dead.
  // Upload UI state is store-driven (survives navigation). Subscribe so any
  // store change re-renders this page — including changes made by an upload
  // that was started on a PREVIOUS visit to this page.
  const [, uploadTick] = React.useReducer((x) => x + 1, 0);
  useEffect(() => {
    uploadStore.listeners.add(uploadTick);
    return () => uploadStore.listeners.delete(uploadTick);
  }, []);
  const upNow = uploadStore.uploading;
  const upPct = uploadStore.progress;
  const preparingFiles = uploadStore.preparing;
  const setPreparingFiles = (v) => uploadStore.set({ preparing: v });
  const setQueuedCount = (v) => uploadStore.set({ queuedCount: v });
  const setBatchCount = () => {}; // batch size no longer displayed
  // Write-through setters: the store is the source of truth; the parent's
  // state is kept in sync as a courtesy (harmless no-op after unmount).
  const setUploadingAll = (v) => { uploadStore.set({ uploading: v }); try { setUploading(v); } catch (_) {} };
  const setProgressAll = (v) => { uploadStore.set({ progress: v }); try { setUploadProgress(v); } catch (_) {} };

  // Core pipeline for ONE batch — used by drag/drop/tap uploads AND the
  // document scanner (which hands it a freshly made PDF).
  const runBatch = async (acceptedFiles) => {
      if (acceptedFiles.length === 0) {
        // Drop contained no usable files (e.g. dragged text) — stand down.
        setPreparingFiles(false);
        setUploadingAll(false);
        return;
      }
      setPreparingFiles(false);
      setUploadingAll(true);
      setProgressAll(0);
      setBatchCount(acceptedFiles.length);
      uploadStore.cancelled = false;
      uploadStore.abort = new AbortController();
      uploadStore.pendingAssets = [];
      const signal = uploadStore.abort.signal;
      const token = localStorage.getItem('token');
      const totalSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0) || 1;
      const loadedBytes = new Array(acceptedFiles.length).fill(0);
      const reportProgress = () => {
        const loaded = loadedBytes.reduce((a, b) => a + b, 0);
        setProgressAll(Math.min(Math.round((loaded / totalSize) * 100), 99));
      };

      const uploadOne = async (file, idx) => {
        let targetFolder = 'documents';
        if (file.type.startsWith('image') || /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(file.name)) {
          targetFolder = 'images';
        } else if (/\.(mp4|mov|avi|wmv|webm|mkv)$/i.test(file.name)) {
          targetFolder = 'videos';
        }

        // 1. Read the TAKEN date on this device, before the file goes
        //    anywhere. EXIF (authoritative) for photos; the file's own
        //    last-modified date as the fallback for videos/documents.
        //    This is recorded separately from the upload date.
        let takenAt = null;
        let takenAtSource = 'fileModified';
        if (targetFolder === 'images') {
          try {
            const exif = await exifr.parse(file, { pick: ['DateTimeOriginal', 'CreateDate'] });
            if (exif && (exif.DateTimeOriginal || exif.CreateDate)) {
              takenAt = exif.DateTimeOriginal || exif.CreateDate;
              takenAtSource = 'exif';
            }
          } catch (_) {}
        }
        if (!takenAt && file.lastModified) takenAt = new Date(file.lastModified);

        // Oversized files (big videos): CHUNKED upload. The old route streamed
        // the whole file through the server, which ran out of memory on 4K
        // videos and failed. Now the file goes up in ~32MB slices — each slice
        // is retried on its own, so a hiccup costs one chunk, not the video.
        if (file.size > LARGE_FILE_LIMIT) {
          const startRes = await axios.post(`${API_BASE}/projects/${id}/media/chunked-start`, {
            fileName: file.name,
            targetFolder,
            size: file.size,
            mimeType: file.type,
            takenAt: takenAt ? takenAt.toISOString() : null,
            takenAtLocal: takenAt ? formatLocalStamp(takenAt) : null,
            takenAtSource,
          }, { headers: { Authorization: token }, signal });

          if (startRes.data.alreadyUploaded) {
            loadedBytes[idx] = file.size;
            reportProgress();
            return;
          }

          const { sessionId, assetId } = startRes.data;
          uploadStore.pendingAssets.push(assetId);
          const CHUNK = 32 * 1024 * 1024;
          let offset = 0;
          while (offset < file.size) {
            if (uploadStore.cancelled) throw new Error('cancelled');
            const slice = file.slice(offset, offset + CHUNK);
            let sent = false, chunkErr = null;
            for (let a = 0; a < 3 && !sent && !uploadStore.cancelled; a++) {
              if (a > 0) await new Promise(r => setTimeout(r, 900 * a));
              try {
                const fd = new FormData();
                fd.append('sessionId', sessionId);
                fd.append('offset', String(offset));
                fd.append('chunk', slice, 'chunk.bin');
                await axios.post(`${API_BASE}/media/chunk`, fd, {
                  headers: { Authorization: token },
                  signal,
                  onUploadProgress: (e) => { loadedBytes[idx] = offset + (e.loaded || 0); reportProgress(); },
                });
                sent = true;
              } catch (e) { chunkErr = e; }
            }
            if (!sent) throw chunkErr || new Error('cancelled');
            offset += slice.size;
            loadedBytes[idx] = offset;
            reportProgress();
          }

          await axios.post(`${API_BASE}/media/chunk/finish`, {
            sessionId, assetId, totalSize: file.size,
          }, { headers: { Authorization: token }, signal });
          uploadStore.pendingAssets = uploadStore.pendingAssets.filter((x) => x !== assetId);
          // First-frame poster so the gallery thumbnail matches the camera roll.
          if (targetFolder === 'videos') {
            try {
              const poster = await makeVideoPoster(file);
              if (poster) {
                const pfd = new FormData();
                pfd.append('poster', poster, 'poster.jpg');
                await axios.post(`${API_BASE}/media/${assetId}/poster`, pfd, { headers: { Authorization: token } });
              }
            } catch (_) { /* poster is best-effort */ }
          }
          loadedBytes[idx] = file.size;
          reportProgress();
          return;
        }

        // Steps 2-4 retry as a unit: Dropbox occasionally rejects bursts of
        // simultaneous writes to one folder ("too busy, try again"), and a
        // fresh upload link is requested on each attempt since links are
        // single-use.
        let lastErr = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          if (uploadStore.cancelled) throw new Error('cancelled');
          if (attempt > 0) {
            await new Promise(r => setTimeout(r, 700 * Math.pow(2, attempt - 1) + Math.random() * 300));
            loadedBytes[idx] = 0;
            reportProgress();
          }
          try {
            // 2. Ask our backend for a direct-to-Dropbox upload link.
            //    Metadata (taken date, upload date, uploader, original
            //    filename) is recorded in the database at this moment.
            const linkRes = await axios.post(`${API_BASE}/projects/${id}/media/upload-link`, {
              fileName: file.name,
              targetFolder,
              size: file.size,
              mimeType: file.type,
              takenAt: takenAt ? takenAt.toISOString() : null,
              takenAtLocal: takenAt ? formatLocalStamp(takenAt) : null,
              takenAtSource,
            }, { headers: { Authorization: token }, signal });

            // Already uploaded in a previous (partially failed) batch —
            // skip it instead of creating a duplicate.
            if (linkRes.data.alreadyUploaded) {
              loadedBytes[idx] = file.size;
              reportProgress();
              return;
            }
            uploadStore.pendingAssets.push(linkRes.data.assetId);

            // 3. Send the bytes straight to Dropbox — our server never carries them.
            await axios.post(linkRes.data.uploadLink, file, {
              headers: { 'Content-Type': 'application/octet-stream' },
              signal,
              onUploadProgress: (e) => { loadedBytes[idx] = e.loaded || 0; reportProgress(); }
            });
            loadedBytes[idx] = file.size;
            reportProgress();

            // 4. Confirm so the record flips from 'pending' to 'ready'.
            await axios.post(`${API_BASE}/media/${linkRes.data.assetId}/complete`, {}, {
              headers: { Authorization: token }
            });
            uploadStore.pendingAssets = uploadStore.pendingAssets.filter((x) => x !== linkRes.data.assetId);
            // First-frame poster so the gallery thumbnail matches the camera roll.
            if (targetFolder === 'videos') {
              try {
                const poster = await makeVideoPoster(file);
                if (poster) {
                  const pfd = new FormData();
                  pfd.append('poster', poster, 'poster.jpg');
                  await axios.post(`${API_BASE}/media/${linkRes.data.assetId}/poster`, pfd, { headers: { Authorization: token } });
                }
              } catch (_) { /* poster is best-effort */ }
            }
            return;
          } catch (e) {
            lastErr = e;
          }
        }
        throw lastErr;
      };

      try {
        // Small worker pool: a few files in flight at once, the rest queue.
        // One file failing (after its own retries) doesn't sink the batch.
        const failedNames = [];
        let nextIdx = 0;
        const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, acceptedFiles.length) }, async () => {
          while (nextIdx < acceptedFiles.length && !uploadStore.cancelled) {
            const idx = nextIdx++;
            try {
              await uploadOne(acceptedFiles[idx], idx);
            } catch (e) {
              if (!uploadStore.cancelled) failedNames.push(acceptedFiles[idx].name);
              loadedBytes[idx] = acceptedFiles[idx].size; // keep the bar honest
              reportProgress();
            }
          }
        });
        await Promise.all(workers);

        // Cancelled: no alerts, no "uploaded" notification — clean up the
        // pending records of unfinished files and stand down quietly.
        if (uploadStore.cancelled) {
          const ids = uploadStore.pendingAssets.splice(0);
          for (const aid of ids) {
            try { await axios.delete(`${API_BASE}/media/${aid}/pending`, { headers: { Authorization: token } }); } catch (_) {}
          }
          setTimeout(() => fetchFiles(), 800);
          return;
        }
        setProgressAll(100);

        const okCount = acceptedFiles.length - failedNames.length;
        if (failedNames.length) {
          alert(`${failedNames.length} of ${acceptedFiles.length} files failed to upload:\n\n${failedNames.join('\n')}\n\nPlease try those again.`);
        }
        // Fire a single "batch complete" notification (shows in the SideNav bell).
        if (okCount > 0) {
          try {
            await axios.post(`${API_BASE}/notifications/self`, {
              title: `${okCount} file${okCount === 1 ? '' : 's'} uploaded`,
              message: 'Documents & media saved to the project.',
              type: 'upload_complete',
              link: `/projects/${id}`,
              project: id,
            }, { headers: { Authorization: token } });
          } catch (_) { /* notification is best-effort */ }
        }
        setTimeout(() => fetchFiles(), 1500);
      } catch (err) {
        if (!uploadStore.cancelled) {
          alert("Upload failed");
          try {
            await axios.post(`${API_BASE}/notifications/self`, {
              title: 'Upload failed',
              message: 'Some files could not be uploaded. Please try again.',
              type: 'info',
              link: `/projects/${id}`,
              project: id,
            }, { headers: { Authorization: token } });
          } catch (_) {}
        }
      } finally {
        setTimeout(() => {
          setUploadingAll(false);
          setProgressAll(0);
        }, 800);
      }
  };

  // Public entry: runs the batch now, or queues it if one is already running,
  // then drains the queue batch by batch.
  const handleFilesUpload = async (acceptedFiles) => {
    const files = (acceptedFiles || []).filter(Boolean);
    if (files.length === 0) { setPreparingFiles(false); return; }
    if (uploadStore.active) {
      uploadStore.queue.push(...files);
      setQueuedCount(uploadStore.queue.length);
      setPreparingFiles(false);
      return;
    }
    uploadStore.active = true;
    try {
      let batch = files;
      while (batch.length && !uploadStore.cancelled) {
        await runBatch(batch);
        batch = uploadStore.cancelled ? [] : uploadStore.queue.splice(0, uploadStore.queue.length);
        setQueuedCount(0);
      }
    } finally {
      uploadStore.active = false;
    }
  };

  // noClick: WE decide what a click does — desktop opens the native picker
  // directly; mobile opens our action sheet (upload vs scan) so there's ONE
  // card instead of two. Drag & drop is unaffected.
  const { getRootProps, getInputProps, isDragActive, open: openFileDialog } = useDropzone({ onDrop: handleFilesUpload, noClick: true, noKeyboard: true });

  // ---- Instant feedback for Photos-app drags ----
  // Dragging out of Photos (or undownloaded iCloud files), macOS exports the
  // real files AFTER the mouse releases — the browser's drop event can arrive
  // many seconds later, which looked like the app ignoring the drop. While a
  // drag hovers, the browser fires dragover continuously (~every 100ms); when
  // that stream goes quiet WITHOUT a dragleave or drop, the user has let go
  // and the files are being prepared — show the upload overlay immediately.
  const dragWatch = useRef({ timer: null, failsafe: null });
  const noteDragOver = () => {
    const w = dragWatch.current;
    if (w.timer) clearTimeout(w.timer);
    w.timer = setTimeout(() => {
      uploadStore.set({ preparing: true });
      // Failsafe: if the drop never lands (drag cancelled in a way that fires
      // no dragleave), stand down instead of spinning forever.
      if (w.failsafe) clearTimeout(w.failsafe);
      w.failsafe = setTimeout(() => {
        if (uploadStore.preparing && !uploadStore.uploading) uploadStore.set({ preparing: false });
      }, 120000);
    }, 400);
  };
  const cancelDragWatch = () => {
    const w = dragWatch.current;
    if (w.timer) { clearTimeout(w.timer); w.timer = null; }
  };
  const [showUploadSheet, setShowUploadSheet] = useState(false);
  const handleUploadZoneClick = () => {
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (coarse) setShowUploadSheet(true);
    else openFileDialog();
  };

  // ==================== SCAN DOCUMENT (mobile) ====================
  // Camera → corner crop/de-warp (reuses the receipt cropper) → PDF → the
  // normal upload pipeline (lands in the project's documents folder).
  const scanInputRef = useRef(null);
  const [scanFile, setScanFile] = useState(null);

  const imageFileToPdf = async (imgFile, baseName) => {
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(imgFile);
    });
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = dataUrl;
    });
    const landscape = img.width > img.height;
    const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const margin = 6;
    const scale = Math.min((pw - margin * 2) / img.width, (ph - margin * 2) / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    doc.addImage(dataUrl, 'JPEG', (pw - w) / 2, (ph - h) / 2, w, h);
    return new File([doc.output('blob')], `${baseName}.pdf`, { type: 'application/pdf' });
  };

  const finishScan = async (imgFile) => {
    setScanFile(null);
    try {
      const d = new Date();
      const p = (n) => String(n).padStart(2, '0');
      const name = `Scan_${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}.${p(d.getMinutes())}.${p(d.getSeconds())}`;
      const pdf = await imageFileToPdf(imgFile, name);
      await handleFilesUpload([pdf]);
    } catch (err) {
      console.error('Scan to PDF failed:', err);
      alert('Could not convert the scan to a PDF.');
    }
  };

  const openVideo = async (path) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/projects/files/link?path=${encodeURIComponent(path)}`, { headers: { Authorization: token } });
      setSelectedVideoUrl(res.data.link);
    } catch (err) {
      alert("Could not load video");
    }
  };

  // Open a document (typically a PDF) in a new tab. Uses the blob pattern
  // (fetch the Dropbox temp link, blob the bytes, open the blob URL) so PDFs
  // render inline in the browser viewer instead of triggering a download —
  // same approach ExpenseLedger uses for receipts.
  const openDocument = async (path) => {
    try {
      const token = localStorage.getItem('token');
      const linkRes = await axios.get(
        `${API_BASE}/projects/files/link?path=${encodeURIComponent(path)}`,
        { headers: { Authorization: token } }
      );
      const fileRes = await fetch(linkRes.data.link);
      if (!fileRes.ok) throw new Error('Fetch failed: ' + fileRes.status);
      const blob = await fileRes.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener');
      // Revoke after the new tab has had time to load
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error('Failed to open document', err);
      alert('Could not open document');
    }
  };

  // mode 'range' (shift): select everything visually between the anchor and the
  // clicked image. mode 'toggle' (alt/option): add/remove just this one.
  const handleImageSelect = (index, mode) => {
    setSelectedDocs([]); // media selection leaves the documents world
    if (mode === 'toggle') {
      setSelectedIndices(prev =>
        prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
      );
      setLastSelectedIndex(index);
      return;
    }
    // range
    if (lastSelectedIndex === null) {
      setSelectedIndices([index]);
      setLastSelectedIndex(index);
      return;
    }
    const aPos = visualOrder.indexOf(lastSelectedIndex);
    const bPos = visualOrder.indexOf(index);
    if (aPos === -1 || bPos === -1) {
      setSelectedIndices([index]);
      setLastSelectedIndex(index);
      return;
    }
    const [lo, hi] = aPos <= bPos ? [aPos, bPos] : [bPos, aPos];
    setSelectedIndices(visualOrder.slice(lo, hi + 1));
    // Keep the anchor so further shift-clicks keep extending from it.
  };

  // Load the project list (PO#s) for the "Move All Selected" menu.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API_BASE}/projects`, { headers: { Authorization: token } })
      .then(res => setGalleryProjects(
        (res.data || [])
          .map(p => ({ _id: p._id, poNumber: p.poNumber, title: p.title }))
          .sort((a, b) => String(a.poNumber).localeCompare(String(b.poNumber)))
      ))
      .catch(() => {});
  }, []);

  // Deselect when clicking outside any image/video tile / the move menu.
  useEffect(() => {
    if (selectedIndices.length === 0 && selectedVideoIdx.length === 0 && moveMenuIndex === null) return;
    const onDocClick = (e) => {
      const inMenu = e.target.closest && e.target.closest('.gallery-move-menu');
      const inTile = e.target.closest && (e.target.closest('.gallery-image-tile') || e.target.closest('.video-card-tile'));
      const inToolbar = e.target.closest && e.target.closest('.gallery-select-toolbar');
      if (inMenu || inToolbar) return; // interacting with the list/toolbar — keep selection
      setMoveMenuIndex(null);        // any click outside the list closes it
      if (!inTile) {                 // clicking truly outside the thumbnails also deselects
        setSelectedIndices([]);
        setSelectedVideoIdx([]);
        setLastSelectedIndex(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [selectedIndices.length, selectedVideoIdx.length, moveMenuIndex, setSelectedIndices, setLastSelectedIndex]);

  // Move every currently-selected image to another project (same images folder).
  // Everything currently selected — gallery images, videos, AND documents.
  const allSelectedMedia = () => [
    ...selectedIndices.map(i => imageFiles[i]).filter(Boolean),
    ...selectedVideoIdx.map(i => videoFiles[i]).filter(Boolean),
    ...selectedDocs.map(p => documentFiles.find(d => d.path_lower === p)).filter(Boolean),
  ];
  const totalSelected = selectedIndices.length + selectedVideoIdx.length + selectedDocs.length;

  // Heart all selected (or un-heart, when every one of them is already liked).
  const likeSelected = () => {
    const paths = allSelectedMedia().map(m => m.path_lower);
    if (!paths.length) return;
    const liked = new Set(likedMedia);
    const allLiked = paths.every(p => liked.has(p));
    allLiked ? patchLikes([], paths) : patchLikes(paths, []);
    setSelectedIndices([]);
    setSelectedVideoIdx([]);
    setSelectedDocs([]);
  };

  // Delete all selected: ONE confirm for the batch. Global (other-project)
  // media is skipped — delete those from their own project.
  const deleteSelected = async () => {
    const media = allSelectedMedia();
    const own = media.filter(m => !m.fromRelated);
    const skipped = media.length - own.length;
    if (!own.length) { alert('Only global media selected — delete those from their own project.'); return; }
    if (!window.confirm(`Delete ${own.length} item${own.length === 1 ? '' : 's'}?${skipped ? `\n(${skipped} global item${skipped === 1 ? '' : 's'} will be skipped.)` : ''}`)) return;
    const token = localStorage.getItem('token');
    for (const m of own) {
      try {
        await axios.delete(`${API_BASE}/projects/${id}/files`, { data: { path: m.path_lower }, headers: { Authorization: token } });
        const thumbPath = m.path_lower.includes('/images/')
          ? m.path_lower.replace('/images/', '/images/thumbnails/').replace(/\.\w+$/, '.webp')
          : m.path_lower.includes('/videos/')
            ? m.path_lower.replace('/videos/', '/videos/thumbnails/').replace(/\.\w+$/, '.webp')
            : null;
        if (thumbPath) {
          await axios.delete(`${API_BASE}/projects/${id}/files`, { data: { path: thumbPath }, headers: { Authorization: token } }).catch(() => {});
        }
      } catch (_) { /* keep going — one failure shouldn't strand the rest */ }
    }
    patchLikes([], own.map(m => m.path_lower)); // drop any hearts on deleted files
    setSelectedIndices([]);
    setSelectedVideoIdx([]);
    setSelectedDocs([]);
    fetchFiles();
    fetchDocFolders(); // deleted docs leave their folders too
  };

  const moveAllSelected = async (targetProjectId, poLabel) => {
    const imgs = selectedIndices.map(i => imageFiles[i]).filter(Boolean);
    if (imgs.length === 0) return;
    if (!window.confirm(`Move ${imgs.length} image${imgs.length > 1 ? 's' : ''} to PO #${poLabel}? They'll leave this project's gallery.`)) return;
    setMovingFiles(true);
    const token = localStorage.getItem('token');
    try {
      for (const im of imgs) {
        await axios.post(`${API_BASE}/media/move`, { path: im.path_lower, targetProjectId }, { headers: { Authorization: token } });
      }
      setMoveMenuIndex(null);
      setSelectedIndices([]);
      setLastSelectedIndex(null);
      await fetchFiles();
    } catch (err) {
      alert('Move failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setMovingFiles(false);
    }
  };

  // 5. COMPONENT RENDER
  return (
    <div className="space-y-8">
      {/* Floating selection toolbar, bottom-right: count + Like + Delete.
          Serves BOTH the gallery and the videos card. */}
      {totalSelected > 0 && (
        <div className="gallery-select-toolbar fixed bottom-6 right-6 z-[60] flex items-center gap-1 bg-gray-900 border border-gray-700 rounded-full shadow-2xl pl-4 pr-1.5 py-1.5">
          <span className="text-sm font-semibold text-white mr-1">{totalSelected} selected</span>
          <button
            onClick={likeSelected}
            title="Like / unlike selected"
            className="p-2.5 rounded-full hover:bg-pink-500/15 text-pink-500 transition-colors"
          >
            <Heart size={17} className={allSelectedMedia().every(m => likedMedia.includes(m.path_lower)) ? 'fill-pink-500' : ''} />
          </button>
          {!isEmployeeViewer && (
          <button
            onClick={deleteSelected}
            title="Delete selected"
            className="p-2.5 rounded-full hover:bg-red-500/15 text-red-500 transition-colors"
          >
            <Trash2 size={17} />
          </button>
          )}
        </div>
      )}

      {/* Upload Dropzone */}
      <div
        {...getRootProps({
          // Release detection (see dragWatch): overlay shows the moment the
          // mouse lets go, even while macOS is still exporting from Photos.
          onDragOver: noteDragOver,
          onDragLeave: cancelDragWatch,
          onDropCapture: () => { cancelDragWatch(); setPreparingFiles(true); },
          onClick: handleUploadZoneClick,
        })}
        className={`border-2 border-dashed rounded-3xl p-16 max-md:p-8 text-center cursor-pointer relative transition-colors ${isDragActive ? 'border-indigo-500 bg-indigo-500/10' : 'border-gray-800 bg-gray-800/10'}`}
      >
        <input {...getInputProps()} />
        {(upNow || preparingFiles) && (
          <div className="absolute inset-0 bg-gray-950/90 flex flex-col items-center justify-center rounded-3xl z-20">
            <Loader2 className="text-indigo-500 animate-spin mb-4" size={48} />
            <div className="text-white text-sm font-black uppercase tracking-widest mb-4 bg-indigo-500/20 px-3 py-1 rounded-full">
              {!upNow ? 'Uploading…' : `Uploading ${upPct}%`}
            </div>
            {uploadStore.queuedCount > 0 && (
              <div className="text-zinc-400 text-xs mb-3">{uploadStore.queuedCount} more file{uploadStore.queuedCount === 1 ? '' : 's'} queued</div>
            )}
            <div className="w-64 h-2 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: upNow ? `${upPct}%` : '0%' }} />
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!window.confirm('Cancel this upload? Files already finished stay; everything else stops.')) return;
                uploadStore.cancel();
              }}
              className="mt-4 px-4 py-2 bg-red-600/80 hover:bg-red-600 text-white text-xs font-bold uppercase tracking-widest rounded-lg z-30"
            >
              Cancel Upload
            </button>
          </div>
        )}
        <Upload className="mx-auto mb-4 max-md:mb-2 text-zinc-600 max-md:w-9 max-md:h-9" size={48} />
        <p className="text-xl font-black text-zinc-300 uppercase max-md:hidden">Drop Assets to Upload</p>
        {/* Mobile: tapping opens the iPhone picker (Photo Library / Take Photo / Files) */}
        <p className="text-base font-black text-zinc-300 uppercase md:hidden">Tap to Upload</p>
        <p className="text-xs text-zinc-600 mt-1 md:hidden">Camera · Photo Library · Files</p>
      </div>

      {/* Mobile action sheet: ONE upload card — tapping it offers Upload or
          Scan instead of showing two separate cards. */}
      {showUploadSheet && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-end justify-center md:hidden"
          onClick={() => setShowUploadSheet(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-gray-900 border-t border-gray-700 rounded-t-3xl w-full max-w-md p-5 space-y-3"
            style={{ paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}
          >
            <p className="text-center text-xs font-black uppercase tracking-widest text-zinc-500 mb-1">Add to project</p>
            <button
              onClick={() => { setShowUploadSheet(false); openFileDialog(); }}
              className="w-full py-4 bg-gray-800 active:bg-gray-700 rounded-2xl text-zinc-100 font-bold flex items-center justify-center gap-3"
            >
              <Upload size={20} className="text-indigo-400" /> Upload Photos & Files
            </button>
            <button
              onClick={() => { setShowUploadSheet(false); scanInputRef.current && scanInputRef.current.click(); }}
              className="w-full py-4 bg-gray-800 active:bg-gray-700 rounded-2xl text-zinc-100 font-bold flex items-center justify-center gap-3"
            >
              <FileText size={20} className="text-red-400" /> Scan Document
            </button>
            <button
              onClick={() => setShowUploadSheet(false)}
              className="w-full py-3 text-zinc-500 font-semibold"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <input
        ref={scanInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = '';
          if (f) setScanFile(f);
        }}
      />
      {scanFile && (
        <SafeReceiptCropper
          file={scanFile}
          remaining={1}
          onDone={(cropped) => finishScan(cropped)}
          onUseOriginal={() => finishScan(scanFile)}
          onCancelAll={() => setScanFile(null)}
        />
      )}

      {/* Folders: Documents, Videos, and Gallery */}
      {/* select-none: no text highlighting anywhere in this card — accidental
          click-drags were painting selections. Copy still works from the
          opened document itself. */}
      <div className="bg-gray-900 rounded-3xl p-8 border border-gray-800/50 shadow-2xl select-none" style={{ WebkitUserSelect: 'none', display: hideDocsCat ? 'none' : undefined }}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-sm font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
            <FileText size={16} className="text-indigo-500" /> Documents
            {openF && (
              <span
                data-docdrop="out"
                className={`normal-case tracking-normal flex items-center gap-2 ml-2 px-2 py-1 rounded-lg ${dropHint === 'out' ? 'bg-indigo-500/20 ring-2 ring-indigo-400' : ''}`}
              >
                <button
                  onClick={() => setOpenFolderId(null)}
                  className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 text-xs font-bold"
                  title={dragDoc ? 'Drop here to move the file OUT of this folder' : 'Back to all documents'}
                >
                  <ChevronLeft size={14} /> Back
                </button>
                <Folder size={14} className="text-indigo-400" />
                {FolderName(openF)}
              </span>
            )}
          </h3>
          <div className="flex items-center gap-3">
            {/* Grid / list view toggle */}
            <button
              onClick={() => { setDocsView('list'); localStorage.setItem('docsViewMode', 'list'); }}
              title="List view"
            >
              <List size={18} className={`transition-colors ${docsView === 'list' ? 'text-indigo-400' : 'text-zinc-700 hover:text-zinc-500'}`} />
            </button>
            <button
              onClick={() => { setDocsView('grid'); localStorage.setItem('docsViewMode', 'grid'); }}
              title="Grid view"
            >
              <LayoutGrid size={18} className={`transition-colors ${docsView === 'grid' ? 'text-indigo-400' : 'text-zinc-700 hover:text-zinc-500'}`} />
            </button>
            {relatedProjects?.length > 0 && (
              <button onClick={() => setIsGlobalViewDocs(!isGlobalViewDocs)} title="Toggle Global Documents">
                <Globe size={18} className={`transition-colors duration-300 ${isGlobalViewDocs ? 'text-[#39ff14]' : 'text-zinc-700 hover:text-zinc-500'}`} />
              </button>
            )}
          </div>
        </div>
        {docsView === 'grid' ? (
          /* GRID: folder tiles + file cards, two across */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!openF && docFolders.map(f => DocTileFolder(f))}
            {docsShown.map(doc => DocTileFile(doc))}
          </div>
        ) : (
          /* LIST: Finder-style — folders expand in place; double-click enters */
          <div className="divide-y divide-gray-800/40">
            {!openF && docFolders.map(f => DocRowFolder(f))}
            {docsShown.map(doc => DocRowFile(doc))}
          </div>
        )}
        {openF && docsShown.length === 0 && (
          <p className="text-zinc-600 text-sm mt-3">This folder is empty — drag files onto it to fill it.</p>
        )}

        {/* iOS-style floating clone that follows the finger/pointer */}
        {dragDoc && (
          <div
            style={{ position: 'fixed', left: dragPos.x, top: dragPos.y, transform: 'translate(-50%, -70%) scale(1.06) rotate(-1.5deg)', zIndex: 130, pointerEvents: 'none' }}
            className="px-3 py-2 bg-gray-800/95 border border-indigo-500/70 rounded-xl shadow-2xl flex items-center gap-2"
          >
            <FileText size={16} className="text-red-500" />
            <span className="text-xs text-zinc-100 max-w-[180px] truncate">
              {dragDoc.count > 1 ? `${dragDoc.count} files` : dragDoc.name}
            </span>
            {dragDoc.count > 1 && (
              <span className="bg-indigo-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">{dragDoc.count}</span>
            )}
          </div>
        )}

        {/* Transient "hands off" note for global-view files */}
        {globalNote && (
          <div
            /* whole-pixel positioning, no transform — fractional coords were
               rendering the text blurry */
            style={{ position: 'fixed', left: Math.round(globalNote.x + 14), top: Math.round(globalNote.y - 34), zIndex: 140, pointerEvents: 'none' }}
            className="px-2.5 py-1.5 bg-gray-900 border border-[#39ff14]/50 rounded-lg text-[11px] text-zinc-200 shadow-xl whitespace-nowrap flex items-center gap-1.5"
          >
            <Globe size={11} className="text-[#39ff14]" /> Global files can't be moved
          </div>
        )}
      </div>

      {/* Easter egg: silent drop target, same as the Gallery card below. */}
      <div
        onDragOver={(e) => {
          if (e.dataTransfer?.types?.includes('Files')) {
            e.preventDefault();
            setVideosDragging(true);
            noteDragOver(); // show the overlay the moment the mouse releases
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) { setVideosDragging(false); cancelDragWatch(); }
        }}
        onDrop={(e) => {
          if (!e.dataTransfer?.files?.length) return;
          e.preventDefault();
          cancelDragWatch();
          setVideosDragging(false);
          setPreparingFiles(true); // instant feedback on the upload card
          handleFilesUpload(Array.from(e.dataTransfer.files));
        }}
        className="relative bg-gray-900 rounded-3xl p-8 border border-gray-800/50 shadow-2xl"
        style={{ display: hideVideosCat ? 'none' : undefined }}
      >
        {videosDragging && (
          <div className="absolute inset-0 z-30 rounded-3xl border-2 border-dashed border-indigo-500 bg-indigo-500/10 pointer-events-none" />
        )}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-sm font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
            <Video size={16} className="text-indigo-500" /> Videos
          </h3>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowLikedVideosOnly(v => !v)} title={showLikedVideosOnly ? 'Show all videos' : 'Show liked videos'}>
              <Heart size={18} className={`transition-colors duration-300 ${showLikedVideosOnly ? 'text-pink-500 fill-pink-500' : 'text-zinc-700 hover:text-zinc-500'}`} />
            </button>
            {relatedProjects?.length > 0 && (
              <button onClick={() => setIsGlobalViewVideos(!isGlobalViewVideos)} title="Toggle Global Videos">
                <Globe size={18} className={`transition-colors duration-300 ${isGlobalViewVideos ? 'text-[#39ff14]' : 'text-zinc-700 hover:text-zinc-500'}`} />
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.entries(groupedVideos).map(([month, vids]) => (
            <React.Fragment key={month}>
              {/* The Subtle Divider — same treatment as the gallery */}
              <div className="col-span-full flex items-center gap-4 mt-2 mb-1 opacity-60 pointer-events-none">
                <span className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">{month}</span>
                <div className="flex-1 h-px bg-zinc-800/60"></div>
              </div>
              {vids.map((video) => (
                <VideoCard
                  key={video.originalIndex}
                  video={video}
                  index={video.originalIndex}
                  isSelected={selectedVideoIdx.includes(video.originalIndex)}
                  isLiked={likedMedia.includes(video.path_lower)}
                  anySelected={selectedVideoIdx.length > 0}
                  onSelect={handleVideoSelect}
                  onOpen={openVideo}
                  onDelete={deleteFile}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Easter egg: the Gallery card itself is a silent drop target — drag
          files from the desktop straight onto it. No text, no hints; just a
          quiet indigo outline while hovering with files. */}
      <div
        onDragOver={(e) => {
          if (e.dataTransfer?.types?.includes('Files')) {
            e.preventDefault();
            setGalleryDragging(true);
            noteDragOver(); // show the overlay the moment the mouse releases
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) { setGalleryDragging(false); cancelDragWatch(); }
        }}
        onDrop={(e) => {
          if (!e.dataTransfer?.files?.length) return;
          e.preventDefault();
          cancelDragWatch();
          setGalleryDragging(false);
          setPreparingFiles(true); // instant feedback on the upload card
          handleFilesUpload(Array.from(e.dataTransfer.files));
        }}
        className="relative bg-gray-900 rounded-3xl p-8 border border-gray-800/50 shadow-2xl"
        style={{ display: hideGalleryCat ? 'none' : undefined }}
      >
        {galleryDragging && (
          <div className="absolute inset-0 z-30 rounded-3xl border-2 border-dashed border-indigo-500 bg-indigo-500/10 pointer-events-none" />
        )}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-sm font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
            <ImageIcon size={16} className="text-indigo-500" /> Gallery
          </h3>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowLikedOnly(v => !v)} title={showLikedOnly ? 'Show all photos' : 'Show liked photos'}>
              <Heart size={18} className={`transition-colors duration-300 ${showLikedOnly ? 'text-pink-500 fill-pink-500' : 'text-zinc-700 hover:text-zinc-500'}`} />
            </button>
            {relatedProjects?.length > 0 && (
              <button onClick={() => setIsGlobalViewGallery(!isGlobalViewGallery)} title="Toggle Global Gallery">
                <Globe size={18} className={`transition-colors duration-300 ${isGlobalViewGallery ? 'text-[#39ff14]' : 'text-zinc-700 hover:text-zinc-500'}`} />
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Object.entries(groupedImages).map(([month, imgs]) => (
            <React.Fragment key={month}>
              
              {/* The Subtle Divider */}
              <div className="col-span-full flex items-center gap-4 mt-2 mb-1 opacity-60 pointer-events-none">
                <span className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">{month}</span>
                <div className="flex-1 h-px bg-zinc-800/60"></div>
              </div>

              {/* The Images for this Month */}
              {imgs.map((img) => (
                <ProjectImage
                  key={img.originalIndex}
                  index={img.originalIndex}
                  img={img}
                  isSelected={selectedIndices.includes(img.originalIndex)}
                  isLiked={likedMedia.includes(img.path_lower)}
                  onToggleLike={(im) => likedMedia.includes(im.path_lower) ? patchLikes([], [im.path_lower]) : patchLikes([im.path_lower], [])}
                  onSelect={handleImageSelect}
                  onPreview={openImage}
                  deleteFile={deleteFile}
                  menuOpen={moveMenuIndex === img.originalIndex}
                  onToggleMenu={setMoveMenuIndex}
                  projects={galleryProjects}
                  onMoveAllSelected={moveAllSelected}
                  selectedCount={selectedIndices.length}
                  moving={movingFiles}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* IMAGE LIGHTBOX CAROUSEL: 20% Smaller centered box */}
      {/* PORTAL: the lightbox renders on <body>, outside every page container —
          no ancestor transform/overflow can trap it below the mobile top bar. */}
      {lightboxImage && createPortal(
        <>
          {/* Backdrop Blur Layer */}
          <div className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm" onClick={closeLightbox} />
          
          <div
            className="fixed inset-0 z-[9999] w-screen h-screen max-md:h-[100dvh] bg-black/95 shadow-2xl flex items-center justify-center overflow-hidden"
            onClick={(e) => {
              if (!hasMoved.current) closeLightbox();
            }}
          >
            <button 
              className="absolute top-6 right-6 text-white/50 hover:text-white bg-white/10 p-3 rounded-full z-50 transition-all"
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            >
              <X size={24} />
            </button>

            <div className="absolute top-6 left-1/2 transform -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full z-50 border border-white/10">
              {Math.round(zoom * 100)}%
            </div>

            <button 
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }} 
              className={`absolute left-4 p-4 text-white/30 hover:text-white z-50 transition-opacity ${zoom > 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
              disabled={zoom > 1}
            >
              <ChevronLeft size={40} />
            </button>

            <div
              className="relative w-full h-full flex items-center justify-center overflow-hidden"
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default', touchAction: 'none' }}
            >
              <img 
                src={lightboxImage} 
                alt="Full size"
                className="max-w-full max-h-full object-contain pointer-events-none"
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center',
                  // No easing while a finger or mouse is ON the image — easing
                  // lags the gesture and feels rubbery. Only zoom-wheel eases.
                  transition: (isDragging || touchActive) ? 'none' : 'transform 0.1s ease-out',
                  willChange: 'transform'
                }}
                draggable={false}
              />
            </div>

            <button 
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }} 
              className={`absolute right-4 p-4 text-white/30 hover:text-white z-50 transition-opacity ${zoom > 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
              disabled={zoom > 1}
            >
              <ChevronRight size={40} />
            </button>
          </div>
        </>,
        document.body
      )}

      {/* Video Lightbox Overlay */}
      {selectedVideoUrl && (
        <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4" onClick={() => setSelectedVideoUrl(null)}>
          <button className="absolute top-8 right-8 text-white/70 hover:text-white bg-white/10 p-4 rounded-full z-10" onClick={() => setSelectedVideoUrl(null)}>
            <X size={32} />
          </button>
          <div className="max-w-5xl w-full">
            <video src={selectedVideoUrl} controls autoPlay className="w-full rounded-3xl shadow-2xl" style={{ maxHeight: '85vh' }}>
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectProfile_DocsMedia;